import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ECHION_SKILL_IMAGES } from "./data/echionSkillImages";
import { echionSkills } from "./data/echionSkills";
import { PATCH_HISTORY } from "./data/patchHistory";
import {
  comparePatchVersions as compareNormalizedPatchVersions,
  normalizePatchList,
  normalizePatchVersion,
} from "./data/patchAliases";
import { NOTICE } from "./data/notice";
import { TACTICAL_META } from "./data/tacticalMeta";
import { TRAIT_META } from "./data/traitMeta";
import { itemAssets } from "./data/itemAssets";
import itemCatalog from "./data/itemCatalog.json";
import { fetchJson } from "./config";
import { UI_TEXT } from "./constants/uiText";
import { calculateStats } from "./utils/calculateStats";
import {
  getDefaultWeaponKeyForType,
  getWeaponAssetsByType,
  weaponAssets,
  weaponTypeOptions,
} from "./data/weaponAssets";
import {
  getItemInfo as resolveItemInfo,
  getSimulatorItemsBySlot,
  normalizeItemList as resolveItemList,
} from "./utils/itemResolver";
import { getItemGradeClass, getItemGradeLabel } from "./utils/itemGrade";
import "./EchionStats.css";

const MATCH_PAGE_LIMIT = 10;
const MATCH_ERROR_LOG_INTERVAL_MS = 60000;
const ITEM_BUILD_LIMIT = 50;
const ITEM_PICK_GRADE_OPTIONS = [
  { label: "\uc2e0\ud654", value: "legend" },
  { label: "\ucd08\uc6d4", value: "mythic" },
];
const REPRESENTATIVE_WEAPON_CODES = {
  deathadder: "131501",
  sidewinder: "131507",
  blackmamba: "131504",
};
const WEAPON_TYPE_LABELS_KO = {
  deathadder: "\ub370\uc2a4\uc560\ub354",
  sidewinder: "\uc0ac\uc774\ub4dc\uc640\uc778\ub354",
  blackmamba: "\ube14\ub799\ub9d8\ubc14",
};
const ITEM_IMAGE_CDN_BASE = "https://cdn.dak.gg/assets/er/game-assets/11.1.0";
const SHOW_RECENT_MATCH_ITEM_CHANGE_FLOW = false;
const ITEM_PICK_RANKING_DISPLAY_SLOTS = new Set(["clothes", "head", "arm", "leg", "1", "2", "3", "4"]);
const ITEM_CHANGE_GRADES = new Set([
  "legend",
  "legendary",
  "전설",
  "mythic",
  "transcendent",
  "transcendence",
  "초월",
  "신화",
]);
const ITEM_CHANGE_EXCLUDE_CODES = new Set();
const ITEM_CHANGE_EXCLUDE_NAME_KEYWORDS = [
  "망원 카메라",
  "정찰 드론",
  "emp 드론",
  "telephoto camera",
  "recon drone",
  "emp drone",
];
const KST_TIME_ZONE = "Asia/Seoul";
const KST_DATE_TIME_FORMATTER = new Intl.DateTimeFormat("ko-KR", {
  timeZone: KST_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});
const itemCatalogByCode = Object.fromEntries(
  itemCatalog
    .map((item) => [String(item.itemCode ?? item.item_code ?? item.code ?? "").trim(), item])
    .filter(([code]) => code)
);
const itemAssetEntries = Object.values(itemAssets);
const itemAssetByEnglishName = buildItemAssetLookup(itemAssetEntries, getItemEnglishNameCandidates);
const itemAssetByDisplayName = buildItemAssetLookup(itemAssetEntries, getItemDisplayNameCandidates);

function useMediaQuery(query) {
  const getMatches = useCallback(() => (
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false
  ), [query]);
  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(query);
    const handleChange = () => setMatches(mediaQuery.matches);

    handleChange();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [query]);

  return matches;
}

function EchionStats() {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [stats, setStats] = useState(null);
  const [weapons, setWeapons] = useState([]);
  const [playerWeaponCounts, setPlayerWeaponCounts] = useState([]);
  const [matches, setMatches] = useState([]);
  const [itemBuilds, setItemBuilds] = useState([]);
  const [itemPickRankings, setItemPickRankings] = useState(null);
  const [selectedItemPickGrade, setSelectedItemPickGrade] = useState("legend");
  const [itemBuildsLoading, setItemBuildsLoading] = useState(false);
  const [itemBuildsError, setItemBuildsError] = useState("");
  const [patches, setPatches] = useState([]);
  const [selectedPatch, setSelectedPatch] = useState("");
  const [isPlayerWeaponCountsOpen, setIsPlayerWeaponCountsOpen] = useState(false);
  const [isRecentMatchesOpen, setIsRecentMatchesOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("matches");
  const [matchFilter, setMatchFilter] = useState("ranked");
  const [selectedWeapon, setSelectedWeapon] = useState("");
  const [matchPage, setMatchPage] = useState(1);
  const [matchPagination, setMatchPagination] = useState({
    limit: MATCH_PAGE_LIMIT,
    page: 1,
    total: 0,
    total_pages: 0,
  });
  const [simulatorLevel, setSimulatorLevel] = useState(1);
  const [equipment, setEquipment] = useState({
    weapon: null,
    chest: null,
    head: null,
    arm: null,
    leg: null,
  });
  const [weaponType, setWeaponType] = useState("deathadder");
  const [weaponKey, setWeaponKey] = useState(() => getDefaultWeaponKeyForType("deathadder"));
  const [nicknameInput, setNicknameInput] = useState("");
  const [nicknameQuery, setNicknameQuery] = useState("");
  const [matchRefreshKey, setMatchRefreshKey] = useState(0);
  const [apiStatus, setApiStatus] = useState({
    error: "",
    isInitialLoading: true,
    isRefreshing: false,
    lastSuccessAt: null,
    retryAttempt: 0,
  });
  const latestMatchRequestIdRef = useRef(0);
  const lastMatchErrorLogAtRef = useRef(0);
  const weaponStatHighlights = useMemo(() => {
    const metricReaders = {
      winRate: (weapon) => Number(weapon.win_rate ?? weapon.winRate),
      avgRp: (weapon) => Number(weapon.avg_rp_gain ?? weapon.avg_rp),
      avgTk: (weapon) => Number(weapon.avg_tk ?? weapon.avgTk),
      avgDamage: (weapon) => Number(weapon.avg_damage ?? weapon.avgDamage),
    };
    const maxValues = Object.fromEntries(
      Object.entries(metricReaders).map(([metric, readValue]) => {
        const values = weapons
          .map(readValue)
          .filter((value) => Number.isFinite(value));

        return [metric, values.length > 0 ? Math.max(...values) : null];
      })
    );

    return weapons.map((weapon) => Object.fromEntries(
      Object.entries(metricReaders).map(([metric, readValue]) => {
        const value = readValue(weapon);
        const maxValue = maxValues[metric];

        return [metric, Number.isFinite(value) && maxValue !== null && value === maxValue];
      })
    ));
  }, [weapons]);

  const beginApiRequest = useCallback(() => {
    setApiStatus((current) => ({
      ...current,
      error: "",
      isInitialLoading: !current.lastSuccessAt,
      isRefreshing: Boolean(current.lastSuccessAt),
      retryAttempt: 0,
    }));
  }, []);

  const markApiRetry = useCallback((retryAttempt) => {
    setApiStatus((current) => ({
      ...current,
      isInitialLoading: !current.lastSuccessAt,
      isRefreshing: Boolean(current.lastSuccessAt),
      retryAttempt,
    }));
  }, []);

  const markApiSuccess = useCallback(() => {
    setApiStatus({
      error: "",
      isInitialLoading: false,
      isRefreshing: false,
      lastSuccessAt: new Date(),
      retryAttempt: 0,
    });
  }, []);

  const markApiFailure = useCallback((message) => {
    setApiStatus((current) => ({
      ...current,
      error: message,
      isInitialLoading: false,
      isRefreshing: false,
      retryAttempt: 0,
    }));
  }, []);

  useEffect(() => {
    let ignore = false;

    async function loadPatches() {
      beginApiRequest();

      try {
        const patchData = await fetchJson("ekion/patches?current_only=true", {
          onRetry: markApiRetry,
        });

        if (ignore) {
          return;
        }

        const patchList = normalizePatchList(Array.isArray(patchData) ? patchData : []);
        setPatches(patchList);
        setSelectedPatch((current) => {
          if (current && patchList.includes(current)) {
            return current;
          }

          return patchList[0] || "";
        });
        markApiSuccess();
      } catch (err) {
        if (ignore) {
          return;
        }

        console.error(err);
        markApiFailure(UI_TEXT.errors.patches);
      }
    }

    loadPatches();

    return () => {
      ignore = true;
    };
  }, [beginApiRequest, markApiFailure, markApiRetry, markApiSuccess]);

  useEffect(() => {
    if (!selectedPatch) {
      return;
    }

    const params = new URLSearchParams({ patch_version: selectedPatch });

    const query = params.toString();

    let ignore = false;

    async function loadSummary() {
      beginApiRequest();

      try {
        const [statsData, weaponsData, playerWeaponCountsData] = await Promise.all([
          fetchJson(`ekion/stats?${query}`, { onRetry: markApiRetry }),
          fetchJson(`ekion/weapons?${query}`, { onRetry: markApiRetry }),
          fetchJson(`ekion/player-weapon-counts?${query}`, { onRetry: markApiRetry }),
        ]);

        if (ignore) {
          return;
        }

        setStats(statsData);
        setWeapons(weaponsData);
        logMetaPointDebug(weaponsData);
        setPlayerWeaponCounts(
          Array.isArray(playerWeaponCountsData)
            ? playerWeaponCountsData.map((row) => ({
              nickname: row.nickname,
              deathadder: row.deathadder,
              sidewinder: row.sidewinder,
              blackmamba: row.blackmamba,
              total: row.total,
            }))
            : []
        );
        markApiSuccess();
      } catch (err) {
        if (ignore) {
          return;
        }

        console.error(err);
        markApiFailure(UI_TEXT.errors.summary);
      }
    }

    loadSummary();

    return () => {
      ignore = true;
    };
  }, [
    beginApiRequest,
    markApiFailure,
    markApiRetry,
    markApiSuccess,
    selectedPatch,
  ]);

  const loadMatches = useCallback(async () => {
    if (!selectedPatch) {
      return;
    }

    const params = new URLSearchParams({
      patch_version: selectedPatch,
      page: String(matchPage),
      limit: String(MATCH_PAGE_LIMIT),
    });

    if (matchFilter !== "all") {
      params.set("game_type", matchFilter);
    }

    if (nicknameQuery) {
      params.set("nickname", nicknameQuery);
    }

    if (selectedWeapon) {
      params.set("weapon", selectedWeapon);
    }

    const requestId = latestMatchRequestIdRef.current + 1;
    latestMatchRequestIdRef.current = requestId;
    beginApiRequest();

    try {
      const matchesData = await fetchJson(`ekion/matches?${params.toString()}`, {
        onRetry: markApiRetry,
      });

      if (requestId !== latestMatchRequestIdRef.current) {
        return;
      }

      const items = Array.isArray(matchesData) ? matchesData : matchesData.items || [];
      setMatches(items);
      setMatchPagination(
        Array.isArray(matchesData)
          ? {
            limit: MATCH_PAGE_LIMIT,
            page: 1,
            total: items.length,
            total_pages: 1,
          }
          : {
            limit: matchesData.limit || MATCH_PAGE_LIMIT,
            page: matchesData.page || matchPage,
            total: matchesData.total || 0,
            total_pages: matchesData.total_pages || 0,
          }
      );
      markApiSuccess();
    } catch (err) {
      if (requestId !== latestMatchRequestIdRef.current) {
        return;
      }

      const now = Date.now();

      if (now - lastMatchErrorLogAtRef.current > MATCH_ERROR_LOG_INTERVAL_MS) {
        console.error(err);
        lastMatchErrorLogAtRef.current = now;
      }
      markApiFailure(UI_TEXT.errors.matches);
    }
  }, [
    beginApiRequest,
    markApiFailure,
    markApiRetry,
    markApiSuccess,
    matchFilter,
    matchPage,
    nicknameQuery,
    selectedWeapon,
    selectedPatch,
  ]);

  useEffect(() => {
    if (activeTab !== "matches" || !isRecentMatchesOpen) {
      return;
    }

    loadMatches();
  }, [activeTab, isRecentMatchesOpen, loadMatches, matchRefreshKey]);

  useEffect(() => {
    if (!selectedPatch || activeTab !== "itemBuilds") {
      return;
    }

    const params = new URLSearchParams({
      patch_version: selectedPatch,
      game_type: matchFilter === "normal" ? "normal" : "ranked",
      limit: String(ITEM_BUILD_LIMIT),
    });

    const selectedWeaponType = getWeaponClassByName(selectedWeapon);

    if (selectedWeaponType) {
      params.set("weapon_type", selectedWeaponType);
    }

    setItemBuildsLoading(true);
    setItemBuildsError("");
    setItemPickRankings(null);

    beginApiRequest();

    Promise.all([
      fetchJson(`ekion/item-builds?${params.toString()}`, {
        onRetry: markApiRetry,
      }),
      fetchJson(`ekion/item-pick-rankings?${getItemPickRankingQuery(params, selectedItemPickGrade)}`, {
        onRetry: markApiRetry,
      }),
    ])
      .then(([buildData, rankingData]) => {
        setItemBuilds(Array.isArray(buildData) ? buildData : []);
        setItemPickRankings(Array.isArray(rankingData) ? rankingData : []);
        markApiSuccess();
      })
      .catch((err) => {
        console.error(err);
        setItemBuildsError(UI_TEXT.errors.itemBuilds);
        markApiFailure(UI_TEXT.errors.itemBuilds);
      })
      .finally(() => {
        setItemBuildsLoading(false);
      });
  }, [
    activeTab,
    beginApiRequest,
    markApiFailure,
    markApiRetry,
    markApiSuccess,
    matchFilter,
    selectedPatch,
    selectedItemPickGrade,
    selectedWeapon,
  ]);

  useEffect(() => {
    setMatchPage(1);
  }, [matchFilter, nicknameQuery, selectedPatch, selectedWeapon]);

  if (!stats) {
    return (
      <div className="echion-loading">
        <div>
          <p>{UI_TEXT.loading.server}</p>
          {apiStatus.retryAttempt > 0 && (
            <p>{UI_TEXT.loading.retry} {apiStatus.retryAttempt}/3</p>
          )}
          {apiStatus.error && <p>{apiStatus.error}</p>}
        </div>
      </div>
    );
  }

  const matchFilters = [
    { value: "ranked", label: UI_TEXT.filters.ranked },
    { value: "normal", label: UI_TEXT.filters.normal },
  ];
  const weaponFilterOptions = [
    { value: "", label: "전체" },
    { value: "deathadder", label: "데스애더" },
    { value: "sidewinder", label: "사이드와인더" },
    { value: "blackmamba", label: "블랙맘바" },
  ];
  const tabs = [
    { value: "matches", label: UI_TEXT.tabs.matches },
    { value: "itemBuilds", label: UI_TEXT.tabs.itemBuilds },
    { value: "simulator", label: UI_TEXT.tabs.simulator },
    { value: "history", label: UI_TEXT.tabs.history },
    { value: "archive", label: "이전 시즌" },
    { value: "notice", label: UI_TEXT.tabs.notice },
  ];
  const paginationPages = getPaginationPages(matchPagination.page, matchPagination.total_pages);
  const sectionTitle = tabs.find((tab) => tab.value === activeTab)?.label || "";

  function handleNicknameSearch(event) {
    event.preventDefault();
    const keyword = nicknameInput.trim();

    setNicknameQuery(keyword);
    setMatchPage(1);
    setMatchRefreshKey((current) => current + 1);
  }

  function handleResetSearch() {
    setNicknameInput("");
    setNicknameQuery("");
    setMatchPage(1);
    setMatchRefreshKey((current) => current + 1);
  }

  function handleResetWeaponFilter() {
    setSelectedWeapon("");
    setMatchPage(1);
  }

  function handleWeaponFilterChange(weaponValue) {
    setSelectedWeapon(weaponValue);
    setMatchPage(1);
    setMatches([]);
    setItemBuilds([]);
    setItemPickRankings(null);
  }

  function renderRecentControls(showNicknameSearch) {
    return (
      <div className="recent-controls">
        <div className="weapon-filter" aria-label="Weapon filter">
          {weaponFilterOptions.map((option) => (
            <button
              className={selectedWeapon === option.value ? "active" : ""}
              key={option.value || "all"}
              type="button"
              onClick={() => handleWeaponFilterChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {showNicknameSearch && (
          <form className="nickname-search" onSubmit={handleNicknameSearch}>
            <input
              placeholder={UI_TEXT.common.nicknameSearch}
              type="search"
              value={nicknameInput}
              onChange={(event) => setNicknameInput(event.target.value)}
            />
            <button type="submit">{UI_TEXT.common.search}</button>
            <button
              className="nickname-reset"
              type="button"
              onClick={handleResetSearch}
            >
              리셋
            </button>
          </form>
        )}
        <div className="match-filter" aria-label={UI_TEXT.matches.gameTypeFilter}>
          {matchFilters.map((filter) => (
            <button
              className={`${filter.value} ${matchFilter === filter.value ? "active" : ""}`}
              key={filter.value}
              type="button"
              onClick={() => setMatchFilter(filter.value)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <main className="echion-page">
      <header className="echion-header">
        <div>
          <p className="eyebrow">Eternal Return</p>
          <h1>{UI_TEXT.summary.title}</h1>
        </div>
        <div className="header-controls">
          <label className="patch-select">
            <span>{UI_TEXT.common.patch}</span>
            <select
              value={selectedPatch}
              onChange={(event) => setSelectedPatch(event.target.value)}
            >
              {patches.map((patch) => (
                <option key={patch} value={patch}>
                  {patch}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      <section className="stat-grid" aria-label={UI_TEXT.summary.statGridLabel}>
        <StatCard title={UI_TEXT.summary.rankedGames} value={stats.ranked_games.toLocaleString()} />
        <StatCard title={UI_TEXT.summary.rankedAvgRank} value={stats.avg_rank.toFixed(2)} />
        <StatCard title={UI_TEXT.summary.rankedWinRate} value={`${(stats.win_rate * 100).toFixed(1)}%`} />
        <StatCard title={UI_TEXT.summary.rankedAvgRp} value={stats.avg_rp_gain.toFixed(1)} />
        <StatCard
          title={UI_TEXT.summary.rankedAvgDamage}
          value={Math.round(stats.avg_damage).toLocaleString()}
        />
      </section>

      <section className="panel weapons-panel">
        <div className="section-heading">
          <h2>{UI_TEXT.summary.weaponStats}</h2>
          {false && selectedWeapon && (
            <button
              type="button"
              onClick={handleResetWeaponFilter}
            >
              무기 필터 해제
            </button>
          )}
        </div>

        <div className="weapon-list">
          {weapons.length > 0 ? (
            weapons.map((weapon, index) => (
              <WeaponCard
                key={weapon.weapon_group}
                weapon={weapon}
                statHighlights={weaponStatHighlights[index]}
                isMobile={isMobile}
              />
            ))
          ) : (
            <div className="empty-panel">{UI_TEXT.summary.noWeaponStats}</div>
          )}
        </div>
      </section>

      <section className="panel recent-matches-panel">
        <div className="section-tabs" aria-label={UI_TEXT.matches.tabLabel}>
          {tabs.map((tab) => (
            <button
              className={`section-tab ${activeTab === tab.value ? "active" : ""}`}
              key={tab.value}
              type="button"
              onClick={() => setActiveTab(tab.value)}
            >
              {tab.label}
              {tab.value === "simulator" && (
                <span className="beta-badge beta-badge-tab">Beta</span>
              )}
            </button>
          ))}
        </div>

        {activeTab !== "notice" && activeTab !== "matches" && (
          <div className="section-heading">
          <h2 className={activeTab === "simulator" ? "simulator-title" : ""}>
            {sectionTitle}
            {activeTab === "simulator" && (
              <span className="beta-badge">Beta</span>
            )}
          </h2>
          {activeTab === "itemBuilds" && renderRecentControls(false)}
          </div>
        )}

        {activeTab === "matches" && (
          <CollapsibleSection
            title={sectionTitle}
            isOpen={isRecentMatchesOpen}
            onToggle={() => setIsRecentMatchesOpen((current) => !current)}
            actions={isRecentMatchesOpen ? renderRecentControls(true) : null}
            className="recent-matches-collapsible"
          >
          <div className="content-section">
            <div className="match-list">
              <div className="match-table-header">
                <div>{UI_TEXT.matches.nickname}</div>
                <div>TK / K / A</div>
                <div>{UI_TEXT.matches.damageInfo}</div>
                <div>{UI_TEXT.matches.loadoutInfo}</div>
              </div>
              {matches.length > 0 ? (
                matches.map((match) => (
                  <MatchCard key={match.match_id} match={match} isMobile={isMobile} />
                ))
              ) : (
                <div className="empty-panel">{UI_TEXT.matches.noRecentMatches}</div>
              )}
            </div>
            {matchPagination.total_pages > 1 && (
              <div className="match-pagination" aria-label={UI_TEXT.matches.recentMatchPage}>
                <button
                  type="button"
                  disabled={matchPagination.page <= 1}
                  onClick={() => setMatchPage((current) => Math.max(1, current - 1))}
                >
                  {UI_TEXT.common.previous}
                </button>
                {paginationPages.map((page) => (
                  <button
                    className={matchPagination.page === page ? "active" : ""}
                    key={page}
                    type="button"
                    onClick={() => setMatchPage(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={matchPagination.page >= matchPagination.total_pages}
                  onClick={() => (
                    setMatchPage((current) => Math.min(matchPagination.total_pages, current + 1))
                  )}
                >
                  {UI_TEXT.common.next}
                </button>
              </div>
            )}
          </div>
          </CollapsibleSection>
        )}

        {activeTab === "itemBuilds" && (
          <div className="content-section item-build-layout">
            <div className="item-build-section">
              {itemBuildsLoading && itemBuilds.length === 0 ? (
                <div className="empty-panel">{UI_TEXT.loading.itemBuilds}</div>
              ) : itemBuilds.length > 0 ? (
                <ItemBuildTable builds={itemBuilds} />
              ) : itemBuildsError ? (
                <div className="empty-panel">{itemBuildsError}</div>
              ) : (
                <ItemBuildTable builds={itemBuilds} />
              )}
            </div>
            <ItemPickRankingPanel
              rankings={itemPickRankings}
              isLoading={itemBuildsLoading}
              selectedGrade={selectedItemPickGrade}
              onGradeChange={setSelectedItemPickGrade}
            />
          </div>
        )}

        {activeTab === "simulator" && (
          <SimulatorPanel
            equipment={equipment}
            level={simulatorLevel}
            onEquipmentChange={setEquipment}
            onLevelChange={setSimulatorLevel}
            onWeaponKeyChange={setWeaponKey}
            onWeaponTypeChange={setWeaponType}
            weaponType={weaponType}
            weaponKey={weaponKey}
          />
        )}

        {activeTab === "history" && (
          <HistoryTimeline history={PATCH_HISTORY} />
        )}

        {activeTab === "archive" && (
          <SeasonArchive isMobile={isMobile} />
        )}

        {activeTab === "notice" && (
          <NoticePanel />
        )}
      </section>

      <section className={`panel player-weapon-counts-panel ${isPlayerWeaponCountsOpen ? "open" : ""}`}>
        <div className="section-heading">
          <button
            className="collapsible-section-title"
            type="button"
            aria-expanded={isPlayerWeaponCountsOpen}
            onClick={() => setIsPlayerWeaponCountsOpen((current) => !current)}
          >
            <span>유저별 무기 플레이 횟수</span>
            <span aria-hidden="true">{isPlayerWeaponCountsOpen ? "▲" : "▼"}</span>
          </button>
          <h2>유저별 무기 플레이 횟수</h2>
        </div>
        {playerWeaponCounts.length > 0 ? (
          <div className="player-weapon-count-list">
            {playerWeaponCounts.map((row) => (
              <PlayerWeaponCountRow key={row.nickname} row={row} isMobile={isMobile} />
            ))}
          </div>
        ) : (
          <div className="empty-panel">표시할 유저별 무기 기록이 없습니다.</div>
        )}
      </section>
    </main>
  );
}

function CollapsibleSection({ title, isOpen, onToggle, actions = null, children, className = "" }) {
  return (
    <div className={`collapsible-section ${className} ${isOpen ? "open" : ""}`}>
      <div className="section-heading collapsible-section-heading">
        <button
          className="collapsible-section-title"
          type="button"
          aria-expanded={isOpen}
          onClick={onToggle}
        >
          <span>{title}</span>
          <span aria-hidden="true">{isOpen ? "▼" : "▶"}</span>
        </button>
        {actions}
      </div>
      {isOpen && (
        <div className="collapsible-section-body">
          {children}
        </div>
      )}
    </div>
  );
}

function StatCard({ title, value }) {
  return (
    <article className="stat-card">
      <div className="stat-title">{title}</div>
      <div className="stat-value">{value}</div>
    </article>
  );
}

function PlayerWeaponCountRow({ row, isMobile = false }) {
  const nickname = row.nickname || "Unknown";
  const deathadder = row.deathadder || 0;
  const sidewinder = row.sidewinder || 0;
  const blackmamba = row.blackmamba || 0;
  const total = row.total || deathadder + sidewinder + blackmamba;
  const playerUrl = `https://dak.gg/er/players/${encodeURIComponent(nickname)}`;

  return (
    <div className="player-weapon-count-row">
      {isMobile ? (
        <span className="player-weapon-count-name player-weapon-count-name-static">
          {nickname}
        </span>
      ) : (
        <a
          className="player-weapon-count-name"
          href={playerUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {nickname}
        </a>
      )}
      <span className="player-weapon-count-separator">:</span>
      <span className="player-weapon-count-total">{total.toLocaleString()}회</span>
      <span className="player-weapon-count-values">
        <span className="weapon-count-paren">(</span>
        <span className="weapon-count-value deathadder">{deathadder}</span>
        <span className="weapon-count-divider">/</span>
        <span className="weapon-count-value sidewinder">{sidewinder}</span>
        <span className="weapon-count-divider">/</span>
        <span className="weapon-count-value blackmamba">{blackmamba}</span>
        <span className="weapon-count-paren">)</span>
      </span>
    </div>
  );
}

function getWeaponClassByName(name) {
  const normalizedName = String(name || "").toLowerCase();

  if (
    normalizedName.includes(UI_TEXT.simulator.weapons.deathadder.toLowerCase())
    || normalizedName.includes("데스애더")
    || normalizedName.includes("deathadder")
  ) {
    return "deathadder";
  }

  if (
    normalizedName.includes(UI_TEXT.simulator.weapons.sidewinder.toLowerCase())
    || normalizedName.includes("사이드와인더")
    || normalizedName.includes("sidewinder")
  ) {
    return "sidewinder";
  }

  if (
    normalizedName.includes(UI_TEXT.simulator.weapons.blackmamba.toLowerCase())
    || normalizedName.includes("블랙맘바")
    || normalizedName.includes("blackmamba")
  ) {
    return "blackmamba";
  }

  return "";
}

function normalizeSearchText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[\s_.-]/g, "");
}

function getWeaponClassFromEquipment(equipment) {
  const equipmentList = Array.isArray(equipment) ? equipment : [];
  const names = equipmentList.map((item) => item.name || "").join(" ");

  return getWeaponClassByName(names);
}

function getItemPickRankingQuery(params, grade) {
  const rankingParams = new URLSearchParams(params);
  rankingParams.delete("limit");

  if (grade) {
    rankingParams.set("grade", grade);
  }

  return rankingParams.toString();
}

function buildDakItemImageUrl(code) {
  const normalizedCode = String(code || "").trim();
  return normalizedCode ? `${ITEM_IMAGE_CDN_BASE}/ItemIcon_${normalizedCode}.png` : "";
}

function applySpecialMeta(item, metaMap) {
  if (!item) {
    return null;
  }

  const code = String(item.code || "");
  const manualMeta = metaMap[code] || {};
  const apiName = item.name && item.name !== "Unknown" && !item.name.includes("(")
    ? item.name
    : "";
  const englishName = item.english_name || manualMeta.english_name || "";
  const manualImage = manualMeta.image || "";
  const displayName = manualMeta.name || apiName || englishName || code;

  return {
    code,
    name: displayName,
    english_name: englishName,
    image: encodeAssetPath(manualImage) || null,
  };
}

function normalizeTransferFlowItems(match) {
  const flowSource = (
    Array.isArray(match?.itemChangeFlow)
      ? match.itemChangeFlow
      : Array.isArray(match?.transferFlow)
      ? match.transferFlow
      : match?.transfer_flow_json !== undefined
        ? match.transfer_flow_json
        : match?.transfer_flow !== undefined
          ? match.transfer_flow
          : match?.requestedItemsFlow !== undefined
            ? match.requestedItemsFlow
            : []
  );
  const rawFlow = parseTransferFlowSource(flowSource);

  const seenCodes = new Set();

  return rawFlow
    .map((item) => {
      if (!item) {
        return null;
      }

      if (typeof item === "string" || typeof item === "number") {
        const code = String(item);
        return {
          code,
          name: code,
          image: "",
          source: "",
          grade: "",
        };
      }

      const code = String(
        item.itemCode
        ?? item.item_code
        ?? item.code
        ?? ""
      );
      const name = (
        item.itemName
        || item.item_name
        || item.name
        || (code ? `Item ${code}` : "")
      );

      if (!name) {
        return null;
      }

      return {
        code,
        name,
        image: encodeAssetPath(item.itemImage || item.item_image || item.image || ""),
        grade: getItemGradeValue(item),
        source: item.source || "",
      };
    })
    .filter((item) => item?.source !== "drone")
    .filter(isDisplayItemChange)
    .filter((item) => {
      if (!item?.code || seenCodes.has(item.code)) {
        return false;
      }

      seenCodes.add(item.code);
      return true;
    })
    .filter(Boolean);
}

function isDisplayItemChange(item) {
  const code = String(item?.code || "").trim();
  if (ITEM_CHANGE_EXCLUDE_CODES.has(code)) {
    return false;
  }

  const name = String(item?.name || "").trim().toLowerCase();
  if (
    ITEM_CHANGE_EXCLUDE_NAME_KEYWORDS.some((keyword) => (
      name.includes(keyword.toLowerCase())
    ))
  ) {
    return false;
  }

  return ITEM_CHANGE_GRADES.has(String(item?.grade || "").trim().toLowerCase());
}

function parseTransferFlowSource(value) {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value !== "string" || !value.trim()) {
    return [];
  }

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    return [];
  }
}

function getTransferSourceLabel(source) {
  if (source === "console") {
    return UI_TEXT.transferFlow.sourceConsole;
  }

  return "";
}

function getItemCode(item) {
  if (item === null || item === undefined) {
    return "";
  }

  if (typeof item === "string" || typeof item === "number") {
    return String(item).trim();
  }

  return String(
    item?.itemCode
    ?? item?.item_code
    ?? item?.code
    ?? item?.id
    ?? item?.itemId
    ?? item?.item_id
    ?? ""
  ).trim();
}

function isUnknownItemName(value, code = "") {
  const text = String(value || "").trim();
  if (!text) {
    return true;
  }

  if (/^Unknown\(/i.test(text)) {
    return true;
  }

  if (code && text === String(code)) {
    return true;
  }

  if (code && text === `Item ${code}`) {
    return true;
  }

  return false;
}

function firstKnownName(values, code) {
  return values.find((value) => !isUnknownItemName(value, code)) || "";
}

function normalizeItemLookupText(value) {
  return String(value || "")
    .replace(/^.*[\\/]/, "")
    .replace(/\.png$/i, "")
    .replace(/^\d+\.\s*/, "")
    .replace(/[_'"`’]/g, " ")
    .replace(/[^0-9a-zA-Z가-힣]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

function buildItemAssetLookup(entries, getCandidates) {
  return entries.reduce((lookup, entry) => {
    getCandidates(entry).forEach((candidate) => {
      const key = normalizeItemLookupText(candidate);
      if (key && !lookup[key]) {
        lookup[key] = entry;
      }
    });

    return lookup;
  }, {});
}

function getItemEnglishNameCandidates(item) {
  if (!item) {
    return [];
  }

  return [
    item.englishName,
    item.english_name,
    item.name,
    item.fileName?.replace(/\.png$/i, ""),
    item.code,
  ].filter(Boolean);
}

function getItemDisplayNameCandidates(item) {
  if (!item) {
    return [];
  }

  return [
    item.matchedName,
    item.koreanName,
    item.displayName,
    item.itemName,
    item.item_name,
    item.name,
  ].filter(Boolean);
}

function findItemAssetByCandidates(candidates, lookup) {
  for (const candidate of candidates) {
    const key = normalizeItemLookupText(candidate);
    if (key && lookup[key]) {
      return lookup[key];
    }
  }

  return null;
}

function getItemCatalogEntry(item, code) {
  if (code && itemCatalogByCode[code]) {
    return itemCatalogByCode[code];
  }

  return typeof item === "object" && item ? item.meta || null : null;
}

function getItemAssetByEntry(item) {
  const code = getItemCode(item);
  const catalogEntry = getItemCatalogEntry(item, code);
  const directCodeAsset = code && itemAssets[code] ? itemAssets[code] : null;

  if (directCodeAsset) {
    return directCodeAsset;
  }

  const englishAsset = findItemAssetByCandidates([
    ...(typeof item === "object" && item ? getItemEnglishNameCandidates(item) : []),
    ...getItemEnglishNameCandidates(catalogEntry),
  ], itemAssetByEnglishName);

  if (englishAsset) {
    return englishAsset;
  }

  return findItemAssetByCandidates([
    ...(typeof item === "object" && item ? getItemDisplayNameCandidates(item) : []),
    ...getItemDisplayNameCandidates(catalogEntry),
  ], itemAssetByDisplayName) || {};
}

function getItemInfo(item, fallbackName = "") {
  return resolveItemInfo(item, fallbackName);

  // eslint-disable-next-line no-unreachable
  const code = getItemCode(item);
  const catalogEntry = getItemCatalogEntry(item, code);
  const assetMeta = getItemAssetByEntry(item);
  const englishName = firstKnownName([
    assetMeta?.englishName,
    assetMeta?.english_name,
    assetMeta?.name,
    ...(typeof item === "object" && item ? getItemEnglishNameCandidates(item) : []),
    ...getItemEnglishNameCandidates(catalogEntry),
  ], code);
  const name = firstKnownName([
    assetMeta?.matchedName,
    assetMeta?.koreanName,
    assetMeta?.displayName,
    catalogEntry?.matchedName,
    catalogEntry?.koreanName,
    catalogEntry?.displayName,
    item?.matchedName,
    item?.koreanName,
    item?.displayName,
    assetMeta?.itemName,
    assetMeta?.item_name,
    catalogEntry?.itemName,
    catalogEntry?.item_name,
    item?.itemName,
    item?.item_name,
    catalogEntry?.name,
    item?.name,
    assetMeta?.name,
    fallbackName,
    englishName,
  ], code) || (code ? `Unknown(${code})` : "");
  const image = encodeAssetPath(
    assetMeta?.image
    ?? assetMeta?.itemImage
    ?? assetMeta?.item_image
    ?? assetMeta?.imageUrl
    ?? assetMeta?.image_url
    ?? item?.itemImage
    ?? item?.item_image
    ?? item?.image
    ?? item?.imageUrl
    ?? item?.image_url
    ?? catalogEntry?.itemImage
    ?? catalogEntry?.item_image
    ?? catalogEntry?.image
    ?? catalogEntry?.imageUrl
    ?? catalogEntry?.image_url
    ?? ""
  );
  const grade = (
    assetMeta?.item_grade
    ?? assetMeta?.itemGrade
    ?? assetMeta?.grade
    ?? assetMeta?.rarity
    ?? assetMeta?.item_rarity
    ?? catalogEntry?.item_grade
    ?? catalogEntry?.itemGrade
    ?? catalogEntry?.grade
    ?? catalogEntry?.rarity
    ?? catalogEntry?.item_rarity
    ?? item?.item_grade
    ?? item?.itemGrade
    ?? item?.grade
    ?? item?.rarity
    ?? item?.item_rarity
    ?? item?.itemRarity
    ?? item?.meta?.item_grade
    ?? item?.meta?.itemGrade
    ?? item?.meta?.grade
    ?? item?.meta?.rarity
    ?? item?.meta?.item_rarity
    ?? ""
  );

  return {
    code,
    englishName,
    english_name: englishName,
    name,
    itemName: name,
    item_name: name,
    image,
    itemImage: image,
    item_image: image,
    grade,
    itemGrade: grade,
    item_grade: grade,
    rarity: item?.rarity ?? assetMeta?.rarity ?? catalogEntry?.rarity,
  };
}

function getItemGradeValue(item) {
  return getItemInfo(item).grade;
}

function normalizeItemEntry(item, fallbackName = "", sourceArea = "") {
  const itemInfo = getItemInfo(
    typeof item === "object" && item ? { ...item, sourceArea } : { code: item, sourceArea },
    fallbackName,
  );

  return {
    ...(typeof item === "object" && item ? item : {}),
    ...itemInfo,
    itemCode: itemInfo.code,
    item_code: itemInfo.code,
  };
}

function normalizeItemList(value, sourceArea = "") {
  return resolveItemList(value, sourceArea);

  // eslint-disable-next-line no-unreachable
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeItemEntry(item)).filter((item) => item.code || item.name);
  }

  if (typeof value === "object") {
    return Object.values(value).map((item) => normalizeItemEntry(item)).filter((item) => item.code || item.name);
  }

  return [];
}

function getMatchEquipmentItems(match) {
  const sources = [
    match?.equipment,
    match?.equipmentItems,
    match?.equipment_items,
    match?.items,
    match?.finalItems,
    match?.final_items,
    match?.itemBuild,
    match?.item_build,
  ];

  for (const source of sources) {
    const normalized = normalizeItemList(source, "recentMatches");
    if (normalized.length > 0) {
      return normalized;
    }
  }

  return [];
}

function getItemDisplayName(item, fallback = "") {
  return getItemInfo(item, fallback).name || fallback;
}

function encodeAssetPath(path) {
  if (!path) {
    return "";
  }

  return encodeURI(path);
}

function getPaginationPages(currentPage, totalPages) {
  if (!totalPages) {
    return [];
  }

  const start = Math.max(1, currentPage - 2);
  const end = Math.min(totalPages, start + 4);
  const adjustedStart = Math.max(1, end - 4);
  const pages = [];

  for (let page = adjustedStart; page <= end; page += 1) {
    pages.push(page);
  }

  return pages;
}

function WeaponTopBuild({ build, label }) {
  const sourceArea = label.toLowerCase().includes("win")
    ? "weaponStatsWinTop"
    : "weaponStatsPickTop";
  const items = normalizeItemList(build?.items, sourceArea).slice(0, 5);
  const isWinTop = label.toLowerCase().includes("win");
  const statLabel = isWinTop ? "승률" : "픽률";
  const statValue = Number(isWinTop ? build?.win_rate : build?.pick_rate);
  const hasBuild = items.length > 0 && build;

  return (
    <div className="weapon-top-build">
      <div className="weapon-top-label">{label}</div>
      {hasBuild ? (
        <>
          <div className="weapon-top-items">
            {items.map((item, index) => (
              <div
                className={`weapon-top-item ${getItemGradeClass(getItemGradeValue(item))}`}
                key={`${label}-${item.code}-${index}`}
              >
                <ItemImage src={item.image} alt={item.name} code={item.code} showTooltip={false} />
                <ItemInfoTooltip item={item} sourceItem={item} />
              </div>
            ))}
          </div>
          <div className="weapon-top-stats">
            <strong>{statLabel} {Number.isFinite(statValue) ? statValue.toFixed(1) : "0.0"}%</strong>
            <small>{Number(build?.count || 0).toLocaleString()}회</small>
          </div>
        </>
      ) : (
        <div className="weapon-top-empty">데이터 부족</div>
      )}
    </div>
  );
}

function WeaponMvpLeaders({ players, limit = 3, isMobile = false }) {
  const rows = Array.isArray(players) ? players.slice(0, limit) : [];

  return (
    <div className="weapon-rp-leaders">
      {rows.length > 0 ? (
        rows.map((player, index) => {
          const patchRpGain = player.patchRpGain ?? player.patch_rp_gain;
          const hasPatchRpGain = patchRpGain !== null && patchRpGain !== undefined && patchRpGain !== "";
          const patchRpGainNumber = Number(patchRpGain || 0);
          const patchRpGainClass = patchRpGainNumber > 0 ? "positive" : patchRpGainNumber < 0 ? "negative" : "neutral";
          const patchRpGainText = hasPatchRpGain
            ? `${patchRpGainNumber > 0 ? "+" : ""}${patchRpGainNumber.toLocaleString()}`
            : "-";
          const weaponGames = Number(player.weaponGames ?? player.weapon_games ?? player.play_count ?? player.games ?? 0);
          const currentRp = Number(player.currentRp ?? player.latestRp ?? player.rp ?? 0);
          const playerUrl = `https://dak.gg/er/players/${encodeURIComponent(player.nickname || "")}`;

          return (
            <div className={`weapon-rp-leader top-${index + 1}`} key={`${player.nickname}-${index}`}>
              <div className="weapon-rp-rank">MVP TOP{index + 1}</div>
              <div className="weapon-rp-player">
                {isMobile ? (
                  <span className="weapon-rp-name weapon-rp-name-static">
                    {player.nickname}
                  </span>
                ) : (
                  <a
                    className="weapon-rp-name"
                    href={playerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {player.nickname}
                  </a>
                )}
                <div className="weapon-mvp-rp-line">
                  <span>RP : {currentRp.toLocaleString()}</span>
                  <strong className={`weapon-mvp-gain ${patchRpGainClass}`}>
                    ({patchRpGainText})
                  </strong>
                </div>
                <div className="weapon-mvp-games-line">
                  <span>플레이 : {weaponGames.toLocaleString()}회</span>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="weapon-rp-empty">MVP -</div>
      )}
    </div>
  );
}

function formatMetaPointScore(score) {
  return typeof score === "number" ? score.toFixed(1) : "데이터 부족";
}

function getMetaPointScoreStyle(metaPoint) {
  const baseStyle = {
    color: "#222222",
    fontWeight: 800,
    textDecoration: "none",
  };

  if (typeof metaPoint !== "number") {
    return baseStyle;
  }

  const score = Math.max(0, Math.min(100, metaPoint));

  if (score >= 85) {
    return {
      color: "#2563EB",
      fontWeight: 800,
      textDecoration: "underline",
    };
  }

  if (score >= 60) {
    return {
      ...baseStyle,
      color: "#2563EB",
    };
  }

  if (score >= 40) {
    return baseStyle;
  }

  if (score > 15) {
    return {
      ...baseStyle,
      color: "#DC2626",
    };
  }

  return {
    color: "#DC2626",
    fontWeight: 800,
    textDecoration: "underline",
  };
}

function clampMetaPointScore(score) {
  if (typeof score !== "number") {
    return 0;
  }

  return Math.max(0, Math.min(100, score));
}

function getMetaPointLogWeaponName(weapon) {
  const key = String(weapon?.weapon || weapon?.weapon_type || weapon?.weapon_group || "").toLowerCase();

  if (key.includes("death") || key.includes("데스")) return "DeathAdder";
  if (key.includes("side") || key.includes("사이드")) return "Sidewinder";
  if (key.includes("black") || key.includes("블랙")) return "BlackMamba";

  return weapon?.weapon_group || weapon?.representative_item_name || "-";
}

function logMetaPointDebug(weaponsData) {
  if (!Array.isArray(weaponsData) || typeof console === "undefined") {
    return;
  }

  weaponsData.forEach((weapon) => {
    const metaPoint = weapon?.metaPoint;
    const scores = metaPoint?.scores || {};

    if (!metaPoint || typeof metaPoint.score !== "number") {
      return;
    }

    console.log(
      `[MetaPoint]\n`
      + `weapon=${getMetaPointLogWeaponName(weapon)}\n`
      + `AvgRpScore=${Number(scores.AvgRpScore || 0).toFixed(1)}\n`
      + `WinScore=${Number(scores.WinScore || 0).toFixed(1)}\n`
      + `AvgTkScore=${Number(scores.AvgTkScore || 0).toFixed(1)}\n`
      + `RankScore=${Number(scores.RankScore || 0).toFixed(1)}\n`
      + `PickScore=${Number(scores.PickScore || 0).toFixed(1)}\n`
      + `MetaPoint=${Number(metaPoint.score || 0).toFixed(1)}`
    );
  });
}

function MetaPointSummaryButton({ metaPoint, isOpen, onToggle }) {
  return (
    <button
      className="metapoint-summary metapoint-summary-inline"
      type="button"
      aria-expanded={isOpen}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
    >
      <span>MetaPoint</span>
      <strong className="metapoint-summary-value">
        <span className="metapoint-summary-patch metapoint-version">{metaPoint?.patchVersion || "-"}</span>
        <span
          className="metapoint-summary-score metapoint-score"
          style={getMetaPointScoreStyle(metaPoint?.score)}
        >
          {formatMetaPointScore(metaPoint?.score)}
        </span>
      </strong>
    </button>
  );
}

function MetaPointCard({ metaPoint, isOpen, onToggle }) {
  return (
    <div className={`metapoint-card ${isOpen ? "open" : ""}`}>
      <MetaPointSummaryButton
        metaPoint={metaPoint}
        isOpen={isOpen}
        onToggle={onToggle}
      />
    </div>
  );
}

function MetaPointDetail({ metaPoint, history }) {
  const rows = Array.isArray(history)
    ? history
      .filter((row) => Number.isFinite(row?.score) && row?.patchVersion)
      .slice()
      .sort((left, right) => compareNormalizedPatchVersions(left.patchVersion, right.patchVersion))
    : [];

  return (
    <div className="metapoint-panel metapoint-detail-panel open">
      <div className="metapoint-detail">
        <div className="metapoint-current">
          <span>{metaPoint?.sampleCount || 0} games</span>
          <span>WR {Number(metaPoint?.winRate || 0).toFixed(1)}%</span>
          <span>RP {Number(metaPoint?.avgRp || 0).toFixed(1)}</span>
          <span>TK {Number(metaPoint?.avgTk || 0).toFixed(1)}</span>
          <span>Rank {Number(metaPoint?.avgRank || 0).toFixed(2)}</span>
          <span>Pick {Number(metaPoint?.pickRate || 0).toFixed(1)}%</span>
        </div>
        <div className="metapoint-history-list">
          {rows.length > 0 ? (
            rows.map((row) => {
              const clampedScore = clampMetaPointScore(row.score);

              return (
                <div className="metapoint-history-row" key={row.patchVersion}>
                  <span className="metapoint-history-patch">{row.patchVersion}</span>
                  <div
                    className="metapoint-bar"
                    aria-label={`${row.patchVersion} MetaPoint ${formatMetaPointScore(row.score)}`}
                  >
                    <span
                      className="metapoint-bar-marker"
                      style={{ left: `${clampedScore}%` }}
                      aria-hidden="true"
                    />
                    {[0, 20, 40, 60, 80, 100].map((tick) => (
                      <span
                        className="metapoint-bar-tick"
                        key={tick}
                        style={{ left: `${tick}%` }}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                  <strong
                    className="metapoint-history-score"
                    style={getMetaPointScoreStyle(row.score)}
                  >
                    {formatMetaPointScore(row.score)}
                  </strong>
                </div>
              );
            })
          ) : (
            <div className="metapoint-history-empty">패치별 데이터 없음</div>
          )}
        </div>
      </div>
    </div>
  );
}

const SLOT_PICK_TOP3_SLOTS = [
  { key: "chest", label: "옷" },
  { key: "head", label: "머리" },
  { key: "arm", label: "팔" },
  { key: "leg", label: "다리" },
];

function SlotPickTop3Panel({ slotPickTop3 }) {
  const hasAnyItem = SLOT_PICK_TOP3_SLOTS.some(({ key, fallbackKey }) => {
    const rows = slotPickTop3?.[key] || (fallbackKey ? slotPickTop3?.[fallbackKey] : null);
    return Array.isArray(rows) && rows.length > 0;
  });
  const slotRows = SLOT_PICK_TOP3_SLOTS.map(({ key, fallbackKey, label }) => ({
    key,
    label,
    rows: (slotPickTop3?.[key] || (fallbackKey ? slotPickTop3?.[fallbackKey] : []) || []).slice(0, 3),
  }));

  return (
    <div className="slot-pick-top3-panel">
      <div className="slot-pick-top3-title">부위별 PICK TOP3 (픽률 / 승률)</div>
      <div className="slot-pick-top3-matrix">
        {slotRows.map((slot) => (
          <div className="slot-pick-top3-column" key={slot.key}>
            <div className="slot-pick-top3-slot-title">{slot.label}</div>
            <div className="slot-pick-top3-column-items">
              {[0, 1, 2].map((index) => {
                const item = slot.rows[index];

                if (!item) {
                  return (
                    <div className="slot-pick-top3-cell empty" key={`${slot.key}-empty-${index}`}>
                      {hasAnyItem ? "-" : "데이터 부족"}
                    </div>
                  );
                }

                const normalizedItem = getItemInfo(item, item.name || item.itemName || item.item_name || "");
                const itemGrade = getItemGradeValue(normalizedItem);
                const pickRate = Number(item.pickRate ?? item.pick_rate ?? 0);
                const rawWinRate = item.winRate ?? item.win_rate;
                const hasWinRate = rawWinRate !== null && rawWinRate !== undefined && rawWinRate !== "";
                const winRate = Number(rawWinRate);
                const winRateText = hasWinRate && Number.isFinite(winRate) ? `${winRate.toFixed(1)}%` : "-";
                const count = Number(item.count || 0);

                return (
                  <div
                    className={`slot-pick-top3-cell ${getItemGradeClass(itemGrade)}`}
                    key={`${slot.key}-${normalizedItem.code || item.itemCode || index}`}
                    title={`${normalizedItem.name}\n픽률 ${pickRate.toFixed(1)}%\n승률 ${winRateText}\n${count.toLocaleString()}회`}
                  >
                    <ItemImage
                      src={normalizedItem.image}
                      alt={normalizedItem.name}
                      code={normalizedItem.code}
                      showTooltip={false}
                    />
                    <span className="slot-pick-top3-rates">
                      <span>{pickRate.toFixed(1)}% / {winRateText}</span>
                    </span>
                    <ItemInfoTooltip item={normalizedItem} sourceItem={item} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function shouldIgnoreWeaponCardToggle(event) {
  return Boolean(event.target.closest(
    "a, button, input, select, textarea, [role='button'], .item-info-tooltip, .item-tooltip"
  ));
}

function WeaponCard({ weapon, statHighlights = {}, isMobile = false }) {
  const [isMetaPointOpen, setIsMetaPointOpen] = useState(false);
  // 기본통계
  const gameCount = Number(weapon.game_count ?? weapon.gameCount ?? weapon.count ?? 0); 
  const pickRateValue = Number(weapon.pick_rate ?? weapon.pickRate ?? 0);
  const winRateValue = Number(weapon.win_rate ?? weapon.winRate ?? 0);
  const pickRate = `${(pickRateValue * 100).toFixed(1)}%`;
  const winRate = `${(winRateValue * 100).toFixed(1)}%`;
  const avgRp = weapon.avg_rp_gain ?? weapon.avg_rp ?? 0;
  const avgRpText = `${avgRp > 0 ? "+" : ""}${Number(avgRp).toFixed(1)}`;
  const avgTk = Number(weapon.avg_tk ?? weapon.avgTk ?? 0).toFixed(1);
  const avgDamage = formatDamageValue(weapon.avg_damage ?? weapon.avgDamage ?? 0);
  // 주력피해
  const mainDamageTypes = weapon.main_damage_types || weapon.mainDamageTypes || [];
  const visibleMainDamageTypes = mainDamageTypes.slice(0, 2);
  const weaponCardClass = getWeaponClassByName(weapon.weapon_group);
  const pickTopBuild = weapon.pick_top_build || weapon.top_builds?.pick_rate;
  const winTopBuild = weapon.win_top_build || weapon.top_builds?.win_rate;
  // MVP
  const mvpPlayers = weapon.mvpPlayers || weapon.mvp_players || weapon.rp_top_players || weapon.top_rp_players;

  return (
    <article
      className={`weapon-card ${weaponCardClass}`}
      data-expanded={isMetaPointOpen ? "true" : "false"}
      tabIndex={0}
      onClick={(event) => {
        if (shouldIgnoreWeaponCardToggle(event)) {
          return;
        }

        setIsMetaPointOpen((current) => !current);
      }}
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") {
          return;
        }

        if (shouldIgnoreWeaponCardToggle(event)) {
          return;
        }

        event.preventDefault();
        setIsMetaPointOpen((current) => !current);
      }}
    >
      <div className="weapon-identity-card">
        <ItemImage
          src={weapon.image}
          alt={weapon.representative_item_name}
          code={weapon.representative_item_code}
        />
        <div className="weapon-name">{weapon.weapon_group}</div>
        <div className="weapon-sub-name">{weapon.representative_item_name}</div>
        <MetaPointCard
          metaPoint={weapon.metaPoint}
          isOpen={isMetaPointOpen}
          onToggle={() => setIsMetaPointOpen((current) => !current)}
        />
      </div>
      <div className="weapon-stats-card">
        <div className="weapon-basic-stats">
          <div className="weapon-stat-line">{UI_TEXT.matches.pickRate} : {pickRate} ({gameCount.toLocaleString()})</div>
          <div className="weapon-stat-line">
            <span>{UI_TEXT.matches.winRate} :</span>
            <span className={statHighlights.winRate ? "weapon-stat-highlight" : "weapon-stat-value"}>{winRate}</span>
          </div>
          <div className="weapon-stat-line">{UI_TEXT.matches.averageRank} : {Number(weapon.avg_rank ?? weapon.avgRank ?? 0).toFixed(2)}</div>
          <div className="weapon-stat-line">
            <span>{UI_TEXT.matches.averageRp} :</span>
            <span className={`weapon-rp ${statHighlights.avgRp ? "weapon-stat-highlight" : ""}`}>{avgRpText}</span>
          </div>
          <div className="weapon-stat-line">
            <span>평균 TK :</span>
            <span className={statHighlights.avgTk ? "weapon-stat-highlight" : "weapon-stat-value"}>{avgTk}</span>
          </div>
          <div className="weapon-stat-line">
            <span>평균 딜량 :</span>
            <span className={statHighlights.avgDamage ? "weapon-stat-highlight" : "weapon-stat-value"}>{avgDamage}</span>
          </div>
        </div>
        <div className="weapon-main-damage">
          <div className="weapon-main-damage-title">주력 피해</div>
          {visibleMainDamageTypes.length > 0 ? (
            <div className="weapon-main-damage-list">
              {visibleMainDamageTypes.map((damageType, index) => (
                <div
                  className={`weapon-main-damage-item rank-${index + 1}`}
                  key={`${damageType.type || damageType.label}-${index}`}
                >
                  <span>{damageType.label}</span>
                  <span>({Number(damageType.rate || 0).toFixed(1)}%)</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="weapon-main-damage-empty">데이터 부족</div>
          )}
        </div>
      </div>
      <div className="weapon-builds">
        <WeaponTopBuild build={pickTopBuild} label="Pick TOP1" />
        <WeaponTopBuild build={winTopBuild} label="Win TOP1" />
      </div>
      <WeaponMvpLeaders players={mvpPlayers} limit={isMetaPointOpen ? 5 : 3} isMobile={isMobile} />
      {isMetaPointOpen && (
        <div className="weapon-metapoint-detail-area">
          <MetaPointDetail
            metaPoint={weapon.metaPoint}
            history={weapon.metaPointHistory}
          />
        </div>
      )}
      {isMetaPointOpen && (
        <div className="weapon-slot-pick-area">
          <SlotPickTop3Panel slotPickTop3={weapon.slotPickTop3 || weapon.slot_pick_top3} />
        </div>
      )}
    </article>
  );
}

function MatchCard({ match, isMobile = false, showPatchVersion = false }) {
  const [isBuildFlowOpen, setIsBuildFlowOpen] = useState(false);
  const deaths = match.deaths || 0;
  const kda = deaths === 0
    ? "Perfect"
    : (((match.kills || 0) + (match.assists || 0)) / deaths).toFixed(2);
  const equipment = getMatchEquipmentItems(match);
  const traits = Array.isArray(match.traits)
    ? match.traits
      .map((trait) => applySpecialMeta(
        trait,
        TRAIT_META,
      ))
      .filter(Boolean)
    : [];
  const tacticalSkill = applySpecialMeta(
    match.tactical_skill,
    TACTICAL_META,
  );
  const nickname = match.nickname || "Unknown";
  const gameType = match.game_type || "normal";
  const isRanked = gameType === "ranked";
  const playerUrl = `https://dak.gg/er/players/${encodeURIComponent(nickname)}`;
  const rank = Number(match.rank);
  const rankClass = rank === 1
    ? "rank-top-1"
    : rank <= 3
      ? "rank-top-3"
      : "rank-normal";
  const rpChange = match.rp_change ?? null;
  const rpDeltaClass = rpChange === null
    ? ""
    : rpChange >= 0
      ? "positive"
      : "negative";
  const rpChangeText = rpChange === null
    ? "-"
    : `${rpChange > 0 ? "+" : ""}${rpChange}`;
  const rpValueText = isRanked && match.rp
    ? Number(match.rp).toLocaleString()
    : "-";
  const playedAtText = formatPlayedAt(match.played_at);
  const visibleEquipment = equipment.slice(0, 5);
  const specialItems = [...traits, ...(tacticalSkill ? [tacticalSkill] : [])];
  const teamKills = match.team_kills ?? ((match.kills || 0) + (match.assists || 0));
  const totalDamage = match.damage || 0;
  const matchWeaponClass = getWeaponClassFromEquipment(equipment);
  const damageItems = [
    { label: UI_TEXT.matches.totalDamage, value: match.damage, showRatio: false },
    { label: UI_TEXT.matches.basicAttack, value: match.basic_damage, showRatio: true },
    { label: UI_TEXT.matches.skillAttack, value: match.skill_damage, showRatio: true },
    { label: UI_TEXT.matches.fixedDamage, value: match.direct_damage, showRatio: true },
    { label: UI_TEXT.matches.itemDamage, value: match.item_damage, showRatio: true },
  ];
  const transferFlowItems = SHOW_RECENT_MATCH_ITEM_CHANGE_FLOW
    ? normalizeTransferFlowItems(match)
    : [];

  return (
    <article className={`match-card ${matchWeaponClass}`}>
      <div className="match-player">
        <span className={`match-type-badge ${gameType}`}>
          {getGameTypeLabel(gameType)}
        </span>
        {isMobile ? (
          <span className="match-player-name match-player-name-static">{nickname}</span>
        ) : (
          <a className="match-player-name" href={playerUrl} target="_blank" rel="noreferrer">
            {nickname}
          </a>
        )}
        <span className={`match-rank ${rankClass}`}>#{match.rank || "-"}</span>
        <span className="match-rp-gain">
          {isRanked ? (
            <>
              <span className="match-rp-base">RP {rpValueText}</span>
              {" "}
              <span className={`match-rp-delta ${rpDeltaClass}`}>{rpChangeText}</span>
            </>
          ) : (
            UI_TEXT.matches.normalRp
          )}
        </span>
        {playedAtText && <span className="match-played-at">{playedAtText}</span>}
        {showPatchVersion && match.patch_version && (
          <span className="match-patch-version">{match.patch_version}</span>
        )}
      </div>
      <div className="match-score" data-kda={kda}>
        <div className="match-tka">
          <div>
            <strong>{teamKills}</strong>
            <span>TK</span>
          </div>
          <div>
            <strong>{match.kills || 0}</strong>
            <span>K</span>
          </div>
          <div>
            <strong>{match.assists || 0}</strong>
            <span>A</span>
          </div>
        </div>
      </div>
      <div className="damage-list">
        {damageItems.map((item) => (
          <DamageLine
            key={item.label}
            label={item.label}
            value={formatDamageValue(item.value)}
            ratio={item.showRatio ? formatDamageRatio(item.value, totalDamage) : null}
          />
        ))}
      </div>
      <div className="match-loadout">
        <div className="equipment-row match-equipment">
            {visibleEquipment.length > 0 ? (
              visibleEquipment.map((item) => {
                const itemGrade = getItemGradeValue(item);

                return (
                <div
                  className={`match-equipment-item ${getItemGradeClass(itemGrade)}`}
                  key={`${match.match_id}-${item.code}`}
                >
                  <ItemImage src={item.image} alt={item.name} code={item.code} showTooltip={false} />
                  <ItemInfoTooltip item={item} sourceItem={item} />
                </div>
              );
            })
          ) : (
            <span>{UI_TEXT.matches.noEquipment}</span>
          )}
        </div>
        <div className="trait-row match-special-row">
          <div className="match-specials">
            {specialItems.length > 0 ? (
              specialItems.map((item) => (
                <MatchMetaItem
                  key={`${match.match_id}-special-${item.code}`}
                  item={item}
                />
              ))
            ) : (
              <MatchMetaItem fallback="-" />
            )}
          </div>
        </div>
      </div>
      {SHOW_RECENT_MATCH_ITEM_CHANGE_FLOW && (
        <div className="build-flow-section">
          <button
            className="build-flow-toggle"
            type="button"
            aria-expanded={isBuildFlowOpen}
            onClick={() => setIsBuildFlowOpen((current) => !current)}
          >
            <span>{isBuildFlowOpen ? UI_TEXT.transferFlow.toggleClose : UI_TEXT.transferFlow.toggleOpen}</span>
            <span aria-hidden="true">{isBuildFlowOpen ? "-" : "+"}</span>
          </button>
          {isBuildFlowOpen && (
            <div className="build-flow-panel">
              <div className="build-flow-title">
                <span>{UI_TEXT.transferFlow.title}</span>
                <small>{UI_TEXT.transferFlow.description}</small>
              </div>
              <BuildFlowGroup
                emptyText={UI_TEXT.transferFlow.empty}
                items={transferFlowItems}
                matchId={match.match_id}
                title={UI_TEXT.transferFlow.groupTitle}
                type="transfer"
              />
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function BuildFlowGroup({ emptyText, items, matchId, title, type }) {
  if (!items.length) {
    return (
      <div className="build-flow-group">
        <div className="build-flow-group-title">{title}</div>
        <div className="build-flow-empty">{emptyText}</div>
      </div>
    );
  }

  return (
    <div className="build-flow-group">
      <div className="build-flow-group-title">{title}</div>
      <div className="build-flow-list" aria-label={title}>
        {items.map((item, index) => (
          <React.Fragment key={`${matchId}-${type}-${item.code || item.name}-${item.source || item.slot || ""}-${index}`}>
            {index > 0 && <span className="build-flow-arrow">&rarr;</span>}
            <div
              className={`build-flow-chip ${getItemGradeClass(item.grade)}`}
            >
              {item.image && (
                <ItemImage src={item.image} alt={item.name} code={item.code} />
              )}
              {type === "transfer" && item.source && (
                <span className="build-flow-source">
                  {getTransferSourceLabel(item.source)}
                </span>
              )}
              <span>{item.name}</span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function ItemBuildTable({ builds }) {
  if (!builds.length) {
    return <div className="empty-panel">{UI_TEXT.items.noItemBuildStats}</div>;
  }

  return (
    <div className="item-build-table">
      <div className="item-build-header">
        <div>#</div>
        <div>{UI_TEXT.items.itemBuild}</div>
        <div>{UI_TEXT.matches.pickRate}</div>
        <div>{UI_TEXT.matches.winRate}</div>
        <div>{UI_TEXT.items.averageRank}</div>
      </div>
      {builds.map((build) => (
        <div className="item-build-row" key={build.items.map((item) => item.code).join("-")}>
          <div className="build-rank">{build.rank}</div>
          <div className="build-items">
            {normalizeItemList(build.items, "itemBuilds").map((item, index) => {
              const itemGrade = getItemGradeValue(item);

              return (
                <div
                  className={`build-item ${getItemGradeClass(itemGrade)}`}
                  key={`${build.rank}-${item.code || item.itemCode}-${index}`}
                >
                  <ItemImage src={item.image} alt={item.name} code={item.code} showTooltip={false} />
                  <div className="build-item-name">{item.name}</div>
                  <ItemInfoTooltip item={item} sourceItem={item} />
                </div>
              );
            })}
          </div>
          <BuildStat label={UI_TEXT.matches.pickRate} subValue={`${build.count.toLocaleString()}${UI_TEXT.items.countSuffix}`} value={formatPercent(build.pick_rate)} />
          <BuildStat label={UI_TEXT.matches.winRate} value={formatPercent(build.win_rate)} />
          <BuildStat label={UI_TEXT.items.averagePlacement} value={Number(build.average_rank || 0).toFixed(2)} />
        </div>
      ))}
    </div>
  );
}

function ItemPickRankingPanel({ isLoading, onGradeChange, rankings, selectedGrade }) {
  const items = Array.isArray(rankings) ? rankings.filter(isDisplayItemPickRanking) : [];

  return (
    <aside className="item-pick-ranking-panel">
      <div className="item-pick-ranking-heading">
        <h3>개별아이템통계</h3>
        <div className="item-pick-grade-filter" aria-label="Item grade filter">
          {ITEM_PICK_GRADE_OPTIONS.map((option) => (
            <button
              className={selectedGrade === option.value ? "active" : ""}
              key={option.value}
              onClick={() => onGradeChange(option.value)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {isLoading && items.length === 0 ? (
        <div className="item-pick-ranking-empty">{UI_TEXT.loading.itemBuilds}</div>
      ) : items.length > 0 ? (
        <ol className="item-pick-ranking-list">
          {items.map((rawItem) => {
            const item = normalizeItemEntry(rawItem, "", "itemPickRankings");
            const itemCode = item.itemCode || item.code;
            const itemName = item.itemName || item.name || `Unknown(${itemCode})`;
            const itemGrade = getItemGradeValue(item);

            return (
              <li className={`item-pick-ranking-item ${getItemGradeClass(itemGrade)}`} key={`${item.slot || "item"}-${itemCode}`}>
                <ItemImage src={item.image} alt={itemName} code={itemCode} showTooltip={false} />
                <ItemInfoTooltip item={item} sourceItem={rawItem} />
                <div className="item-pick-ranking-body">
                  <div className={`item-pick-ranking-name ${getItemGradeClass(itemGrade)}`}>
                    {itemName}
                  </div>
                  <div className="item-pick-ranking-meta">
                    픽률: {formatPercent(item.pickRate ?? item.pick_rate)} ({Number(item.count || 0).toLocaleString()}회)
                  </div>
                  <div className="item-pick-ranking-meta">
                    승률: {formatPercent(item.winRate ?? item.win_rate)}
                  </div>
                  <div className="item-pick-ranking-meta">
                    평균 등수: {Number(item.averageRank ?? item.average_rank ?? 0).toFixed(2)}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      ) : (
        <div className="item-pick-ranking-empty">표시할 개별아이템통계가 없습니다.</div>
      )}
    </aside>
  );
}

function isDisplayItemPickRanking(item) {
  const slot = String(item?.slot ?? "").trim().toLowerCase();
  return ITEM_PICK_RANKING_DISPLAY_SLOTS.has(slot);
}

function BuildStat({ label, subValue, value, valueClassName = "" }) {
  return (
    <div className="build-stat">
      <span className="build-stat-line">
        <span className="build-stat-label">{label} :</span>
        <span className={`build-stat-value ${valueClassName}`}>{value}</span>
      </span>
      {subValue && <span className="build-stat-sub">{subValue}</span>}
    </div>
  );
}

function getPassiveDisplayName(weaponType) {
  const labels = {
    deathadder: "카드모스의 부름 [데스애더]",
    sidewinder: "카드모스의 부름 [사이드와인더]",
    blackmamba: "카드모스의 부름 [블랙맘바]",
  };

  return labels[weaponType] || labels.deathadder;
}

function getRepresentativeWeaponAsset(weaponType) {
  const representativeCode = REPRESENTATIVE_WEAPON_CODES[weaponType];

  return Object.values(weaponAssets).find((weapon) => (
    weapon.itemCode === representativeCode || weapon.code === representativeCode
  )) || getWeaponAssetsByType(weaponType)[0] || null;
}

function SimulatorPanel({
  equipment,
  level,
  onEquipmentChange,
  onLevelChange,
  onWeaponKeyChange,
  onWeaponTypeChange,
  weaponKey,
  weaponType,
}) {
  const [activeEquipmentSlot, setActiveEquipmentSlot] = useState(null);
  const [itemSearch, setItemSearch] = useState("");
  const [selectedSkillKey, setSelectedSkillKey] = useState("Q");
  const [skillLevels, setSkillLevels] = useState({
    Q: 1,
    W: 1,
    E: 1,
    R: 1,
    P: 3,
  });
  const [showDpsPanel, setShowDpsPanel] = useState(true);
  const [showSkillPanel, setShowSkillPanel] = useState(false);
  const [showItemEffectPanel, setShowItemEffectPanel] = useState(false);
  const [vfGauge, setVfGauge] = useState(100);
  const equipmentSlots = [
    { key: "weapon", label: UI_TEXT.simulator.equipmentSlots.weapon },
    { key: "chest", label: UI_TEXT.simulator.equipmentSlots.armor },
    { key: "head", label: UI_TEXT.simulator.equipmentSlots.head },
    { key: "arm", label: UI_TEXT.simulator.equipmentSlots.arm },
    { key: "leg", label: UI_TEXT.simulator.equipmentSlots.leg },
  ];
  const weaponVariants = useMemo(() => getWeaponAssetsByType(weaponType), [weaponType]);
  const selectedWeaponAsset = (
    weaponAssets[weaponKey]?.baseWeaponType === weaponType
      ? weaponAssets[weaponKey]
      : weaponVariants[0]
  ) || null;
  const equipmentForStats = useMemo(() => ({
    ...equipment,
    weapon: selectedWeaponAsset,
  }), [equipment, selectedWeaponAsset]);
  const calculatedStats = useMemo(() => (
    calculateStats({ level, equipment: equipmentForStats })
  ), [equipmentForStats, level]);
  const { baseStats, equipmentBonus, finalStats } = calculatedStats;
  const passiveDescriptions = {
    deathadder: UI_TEXT.simulator.passiveDescriptions.deathadder,
    sidewinder: UI_TEXT.simulator.passiveDescriptions.sidewinder,
    blackmamba: UI_TEXT.simulator.passiveDescriptions.blackmamba,
  };
  const rImage = ECHION_SKILL_IMAGES.R[weaponType] || ECHION_SKILL_IMAGES.R.deathadder;
  const passiveImage = (
    ECHION_SKILL_IMAGES.passive[weaponType]
    || ECHION_SKILL_IMAGES.passive.deathadder
  );
  const passiveDescription = (
    passiveDescriptions[weaponType]
    || passiveDescriptions.deathadder
  );
  const simulatorSkills = [
    { key: "Q", name: UI_TEXT.simulator.skills.q, image: ECHION_SKILL_IMAGES.Q },
    { key: "W", name: UI_TEXT.simulator.skills.w, image: ECHION_SKILL_IMAGES.W },
    { key: "E", name: UI_TEXT.simulator.skills.e, image: ECHION_SKILL_IMAGES.E },
    { key: "R", name: UI_TEXT.simulator.skills.r, image: rImage },
    {
      key: "P",
      name: getPassiveDisplayName(weaponType),
      description: passiveDescription,
      image: passiveImage,
    },
  ];
  const activeEquipmentItems = useMemo(() => {
    if (!activeEquipmentSlot) {
      return [];
    }

    const searchText = normalizeSearchText(itemSearch);
    const slotItems = activeEquipmentSlot === "weapon"
      ? weaponVariants
      : getSimulatorItemsBySlot(activeEquipmentSlot);

    if (!searchText) {
      return slotItems;
    }

    return slotItems.filter((item) => (
      normalizeSearchText(getItemDisplayName(item)).includes(searchText)
      || normalizeSearchText(item.name).includes(searchText)
      || normalizeSearchText(item.code).includes(searchText)
      || normalizeSearchText(item.weaponKey).includes(searchText)
      || normalizeSearchText(item.baseWeaponType).includes(searchText)
    ));
  }, [activeEquipmentSlot, itemSearch, weaponVariants]);
  const activeItemSkillEffects = useMemo(() => getActiveItemSkillEffects(
    [
      selectedWeaponAsset,
      equipment.chest,
      equipment.head,
      equipment.arm,
      equipment.leg,
    ],
    { level, totalStats: finalStats }
  ), [equipment, finalStats, level, selectedWeaponAsset]);

  function handleLevelChange(value) {
    const nextLevel = Math.min(20, Math.max(1, Number(value) || 1));
    onLevelChange(nextLevel);
  }

  function handleVfGaugeChange(value) {
    setVfGauge(Math.min(100, Math.max(0, Number(value) || 0)));
  }

  function handleEquipmentClick(slotKey) {
    const slotItems = slotKey === "weapon"
      ? weaponVariants
      : getSimulatorItemsBySlot(slotKey);

    if (!slotItems.length) {
      return;
    }

    setItemSearch("");
    setActiveEquipmentSlot(slotKey);
  }

  function handleEquipmentSelect(slotKey, item) {
    if (slotKey === "weapon") {
      onWeaponKeyChange(item.weaponKey);
      setActiveEquipmentSlot(null);
      return;
    }

    onEquipmentChange((current) => ({
      ...current,
      [slotKey]: item,
    }));
    setActiveEquipmentSlot(null);
  }

  function handleEquipmentClear(slotKey) {
    if (slotKey === "weapon") {
      onWeaponKeyChange(getDefaultWeaponKeyForType(weaponType));
      setActiveEquipmentSlot(null);
      return;
    }

    onEquipmentChange((current) => ({
      ...current,
      [slotKey]: null,
    }));
    setActiveEquipmentSlot(null);
  }

  function getSkillLevelBounds(skillKey) {
    const skillData = getEchionSkillData(skillKey, weaponType);
    const fixedLevel = skillData.fixedLevel || (skillData.isPassive ? 1 : null);

    if (fixedLevel) {
      return { fixedLevel, maxLevel: fixedLevel, minLevel: fixedLevel };
    }

    return {
      fixedLevel: null,
      maxLevel: skillData.maxLevel || (skillKey === "R" ? 3 : 5),
      minLevel: 1,
    };
  }

  function handleSkillLevelChange(skillKey, delta) {
    const bounds = getSkillLevelBounds(skillKey);

    if (bounds.fixedLevel) {
      return;
    }

    setSkillLevels((current) => ({
      ...current,
      [skillKey]: Math.min(
        bounds.maxLevel,
        Math.max(bounds.minLevel, (current[skillKey] || bounds.minLevel) + delta)
      ),
    }));
  }

  return (
    <div className="content-section simulator-layout">
      <div className="simulator-top">
        <div className="level-control">
          <div className="level-label">Lv. {level}</div>
          <div className="level-actions">
            <button
              type="button"
              disabled={level <= 1}
              onClick={() => handleLevelChange(level - 1)}
            >
              -
            </button>
            <input
              min="1"
              max="20"
              type="range"
              value={level}
              onChange={(event) => handleLevelChange(event.target.value)}
            />
            <button
              type="button"
              disabled={level >= 20}
              onClick={() => handleLevelChange(level + 1)}
            >
              +
            </button>
          </div>
        </div>
      </div>

      <div className="simulator-main">
        <aside className="simulator-stats-panel">
          <div className="simulator-panel-title">{UI_TEXT.simulator.finalStats}</div>
          <SimulatorStatRow
            label={UI_TEXT.simulator.hp}
            baseValue={baseStats.hp}
            bonusValue={equipmentBonus.hp}
            finalValue={finalStats.hp}
          />
          <SimulatorStatRow
            label={UI_TEXT.simulator.attackPower}
            baseValue={baseStats.attackPower}
            bonusValue={equipmentBonus.attackPower}
            finalValue={finalStats.attackPower}
          />
          <SimulatorStatRow
            label={UI_TEXT.simulator.defense}
            baseValue={baseStats.defense}
            bonusValue={equipmentBonus.defense}
            finalValue={finalStats.defense}
          />
          <SimulatorStatRow
            label={"\ubc29\uc5b4 \uad00\ud1b5"}
            baseValue={baseStats.armorPenetration}
            bonusValue={equipmentBonus.armorPenetration}
            finalValue={finalStats.armorPenetration}
            showBreakdown={false}
          />
          <SimulatorStatRow
            label={"\ubc29\uc5b4 \uad00\ud1b5 %"}
            baseValue={baseStats.armorPenetrationPercent}
            bonusValue={equipmentBonus.armorPenetrationPercent}
            finalValue={finalStats.armorPenetrationPercent}
            showBreakdown={false}
            suffix="%"
          />
          <SimulatorStatRow
            label={"\uce58\uba85\ud0c0 \ud655\ub960"}
            baseValue={baseStats.critChancePercent}
            bonusValue={equipmentBonus.critChancePercent}
            finalValue={finalStats.critChancePercent}
            showBreakdown={false}
            suffix="%"
          />
          <SimulatorStatRow
            label={"\uce58\uba85\ud0c0 \ud53c\ud574\ub7c9"}
            baseValue={baseStats.critDamagePercent}
            bonusValue={equipmentBonus.critDamagePercent}
            finalValue={finalStats.critDamagePercent}
            suffix="%"
          />
          <SimulatorStatRow
            label={"\uae30\ubcf8 \uacf5\uaca9 \uc99d\ud3ed"}
            baseValue={baseStats.basicAttackAmpPercent}
            bonusValue={equipmentBonus.basicAttackAmpPercent}
            finalValue={finalStats.basicAttackAmpPercent}
            suffix="%"
          />
          <SimulatorStatRow
            label={"\ucfe8\ub2e4\uc6b4 \uac10\uc18c"}
            baseValue={0}
            bonusValue={getCooldownReductionPercent(finalStats)}
            finalValue={getCooldownReductionPercent(finalStats)}
            suffix="%"
          />
        </aside>

        <div className="simulator-board">
          <div className="weapon-type-selector" aria-label={UI_TEXT.simulator.weaponTypeLabel}>
            {weaponTypeOptions.map((option) => {
              const representativeWeapon = getRepresentativeWeaponAsset(option.weaponType);
              const weaponName = getItemDisplayName(representativeWeapon, option.name);
              const weaponTypeLabel = WEAPON_TYPE_LABELS_KO[option.weaponType] || option.name;

              return (
                <button
                  className={weaponType === option.weaponType ? "active" : ""}
                  key={option.weaponType}
                  type="button"
                  onClick={() => {
                    onWeaponTypeChange(option.weaponType);
                    onWeaponKeyChange(getDefaultWeaponKeyForType(option.weaponType));
                  }}
                >
                  <ItemImage
                    src={representativeWeapon?.image}
                    alt={weaponName}
                    code={representativeWeapon?.code || representativeWeapon?.weaponKey}
                    useDakFallback={false}
                  />
                  <span className="weapon-type-name">{weaponTypeLabel}</span>
                </button>
              );
            })}
          </div>

          <div className="equipment-slots" aria-label={UI_TEXT.simulator.equipmentSlotLabel}>
            {equipmentSlots.map((slot) => {
              const slotItem = slot.key === "weapon"
                ? selectedWeaponAsset
                : equipment[slot.key];
              const itemGrade = getItemGradeValue(slotItem);
              const equipmentName = getItemDisplayName(slotItem, slot.label);
              const itemStatRows = getItemStatRows(slotItem?.stats);
              const itemSkillNames = getItemSkillNames(slotItem);

              return (
                <button
                  className={`equipment-slot ${getItemGradeClass(itemGrade)}`}
                  key={slot.key}
                  type="button"
                  onClick={() => handleEquipmentClick(slot.key)}
                >
                  <ItemImage
                    src={slotItem?.image}
                    alt={equipmentName}
                    code={slotItem?.code || slotItem?.weaponKey}
                    useDakFallback={slot.key !== "weapon"}
                  />
                  <span className="equipment-slot-body">
                    <span className="equipment-slot-name">{equipmentName}</span>
                    {slotItem && itemStatRows.length > 0 ? (
                      <span className="equipment-slot-stat-list">
                        {itemStatRows.map((row) => (
                          <span className="equipment-slot-stat" key={row.key}>
                            - {row.label} {row.value}
                          </span>
                        ))}
                      </span>
                    ) : null}
                    {slotItem ? itemSkillNames.map((skillName) => (
                      <span className="equipment-slot-skill" key={skillName}>
                        - {skillName}
                      </span>
                    )) : null}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="vf-gauge-control">
            <label htmlFor="vf-gauge-input">VF 게이지: {vfGauge}</label>
            <input
              id="vf-gauge-input"
              max="100"
              min="0"
              type="range"
              value={vfGauge}
              onChange={(event) => handleVfGaugeChange(event.target.value)}
            />
            <input
              max="100"
              min="0"
              type="number"
              value={vfGauge}
              onChange={(event) => handleVfGaugeChange(event.target.value)}
            />
          </div>

          <div className="skill-icons" aria-label={UI_TEXT.simulator.skillIconLabel}>
            {simulatorSkills.map((skill) => (
              <div className="skill-card" key={skill.key}>
                <button
                  className={`skill-icon ${selectedSkillKey === skill.key ? "active" : ""}`}
                  title={skill.description ? `${skill.name} - ${skill.description}` : skill.name}
                  type="button"
                  onClick={() => setSelectedSkillKey(skill.key)}
                >
                  {skill.image ? (
                    <img src={skill.image} alt={skill.name} />
                  ) : (
                    <span>{skill.key}</span>
                  )}
                </button>
                <SkillLevelControl
                  level={skillLevels[skill.key] || (skill.key === "P" ? 3 : 1)}
                  weaponType={weaponType}
                  skillKey={skill.key}
                  onChange={handleSkillLevelChange}
                />
              </div>
            ))}
          </div>

          <CombatPredictionPanel
            isOpen={showDpsPanel}
            onToggle={() => setShowDpsPanel((current) => !current)}
            selectedSkillKey={selectedSkillKey}
            skillLevels={skillLevels}
            totalStats={finalStats}
            vfGauge={vfGauge}
            weaponType={weaponType}
          />

          <SkillDetailPanel
            isOpen={showSkillPanel}
            onToggle={() => setShowSkillPanel((current) => !current)}
            selectedSkillKey={selectedSkillKey}
            skillLevels={skillLevels}
            totalStats={finalStats}
            vfGauge={vfGauge}
            weaponType={weaponType}
          />
          <ActiveItemSkillEffectsPanel
            effects={activeItemSkillEffects}
            isOpen={showItemEffectPanel}
            onToggle={() => setShowItemEffectPanel((current) => !current)}
          />
        </div>
      </div>

      {activeEquipmentSlot && (
        <EquipmentSelectModal
          currentItem={activeEquipmentSlot === "weapon"
            ? selectedWeaponAsset
            : equipment[activeEquipmentSlot]}
          items={activeEquipmentItems}
          searchValue={itemSearch}
          slotLabel={equipmentSlots.find((slot) => slot.key === activeEquipmentSlot)?.label}
          slotKey={activeEquipmentSlot}
          onClose={() => setActiveEquipmentSlot(null)}
          onClear={handleEquipmentClear}
          onSearchChange={setItemSearch}
          onSelect={handleEquipmentSelect}
        />
      )}
    </div>
  );
}

const ITEM_STAT_LABELS = {
  attackPower: "\uacf5\uaca9\ub825",
  defense: "\ubc29\uc5b4\ub825",
  maxHp: "\uccb4\ub825",
  hp: "\uccb4\ub825",
  maxSp: "\uc2a4\ud0dc\ubbf8\ub098",
  hpRegen: "\uccb4\ub825 \uc7ac\uc0dd",
  spRegen: "\uc2a4\ud0dc\ubbf8\ub098 \uc7ac\uc0dd",
  skillAmp: "\uc2a4\ud0ac \uc99d\ud3ed",
  attackSpeed: "\uacf5\uaca9 \uc18d\ub3c4",
  attackSpeedPercent: "\uacf5\uaca9 \uc18d\ub3c4",
  cooldownReduction: "\ucfe8\ub2e4\uc6b4 \uac10\uc18c",
  cooldownReductionPercent: "\ucfe8\ub2e4\uc6b4 \uac10\uc18c",
  moveSpeed: "\uc774\ub3d9 \uc18d\ub3c4",
  moveSpeedPercent: "\uc774\ub3d9 \uc18d\ub3c4",
  moveSpeedFlat: "\uc774\ub3d9 \uc18d\ub3c4",
  critChance: "\uce58\uba85\ud0c0 \ud655\ub960",
  critChancePercent: "\uce58\uba85\ud0c0 \ud655\ub960",
  criticalStrikeChance: "\uce58\uba85\ud0c0 \ud655\ub960",
  critDamage: "\uce58\uba85\ud0c0 \ud53c\ud574\ub7c9",
  critDamagePercent: "\uce58\uba85\ud0c0 \ud53c\ud574\ub7c9",
  criticalStrikeDamage: "\uce58\uba85\ud0c0 \ud53c\ud574\ub7c9",
  armorPenetration: "\ubc29\uc5b4 \uad00\ud1b5",
  armorPenetrationPercent: "\ubc29\uc5b4 \uad00\ud1b5",
  skillAmpPenetration: "\uc2a4\ud0ac \uc99d\ud3ed \uad00\ud1b5",
  skillAmpPenetrationPercent: "\uc2a4\ud0ac \uc99d\ud3ed \uad00\ud1b5",
  healingReduction: "\uce58\uc720 \uac10\uc18c",
  lifeSteal: "\ud761\ud608",
  lifeStealPercent: "\uc0dd\uba85\ub825 \ud761\uc218",
  omniVampPercent: "\ubaa8\ub4e0 \ud53c\ud574 \ud761\ud608",
  omnivamp: "\ubaa8\ub4e0 \ud53c\ud574 \ud761\ud608",
  basicAttackRange: "\uae30\ubcf8 \uacf5\uaca9 \uc0ac\uac70\ub9ac",
  basicAttackAmpPercent: "\uae30\ubcf8 \uacf5\uaca9 \uc99d\ud3ed",
  crowdControlResistance: "\ubc29\ud574 \ud6a8\uacfc \uc800\ud56d",
  crowdControlResistancePercent: "\ubc29\ud574 \ud6a8\uacfc \uc800\ud56d",
  attackPowerPerLevel: "\ub808\ubca8\ub2f9 \uacf5\uaca9\ub825",
  defensePerLevel: "\ub808\ubca8\ub2f9 \ubc29\uc5b4\ub825",
  maxHpPerLevel: "\ub808\ubca8\ub2f9 \uccb4\ub825",
  skillAmpPerLevel: "\ub808\ubca8\ub2f9 \uc2a4\ud0ac \uc99d\ud3ed",
  basicAttackAmpPerLevel: "\ub808\ubca8\ub2f9 \uae30\ubcf8 \uacf5\uaca9 \uc99d\ud3ed",
  attackPowerPerLevelRange: "\ub808\ubca8\ub2f9 \uacf5\uaca9\ub825",
  skillAmpPerLevelRange: "\ub808\ubca8\ub2f9 \uc2a4\ud0ac \uc99d\ud3ed",
  maxHpPerLevelRange: "\ub808\ubca8\ub2f9 \uccb4\ub825",
};

const ITEM_STAT_RANGE_PAIRS = [
  {
    labelKey: "attackPowerPerLevelRange",
    maxKey: "attackPowerPerLevelMax",
    minKey: "attackPowerPerLevelMin",
    suffix: "",
  },
  {
    labelKey: "maxHpPerLevelRange",
    maxKey: "maxHpPerLevelMax",
    minKey: "maxHpPerLevelMin",
    suffix: "",
  },
  {
    labelKey: "basicAttackAmpPerLevel",
    maxKey: "basicAttackAmpPerLevelMax",
    minKey: "basicAttackAmpPerLevelMin",
    suffix: "%",
  },
  {
    labelKey: "skillAmpPerLevelRange",
    maxKey: "skillAmpPerLevelMax",
    minKey: "skillAmpPerLevelMin",
    suffix: "",
  },
];

const ITEM_STAT_DISPLAY_ORDER = [
  "attackPower",
  "attackPowerPerLevel",
  "attackPowerPerLevelRange",
  "maxHp",
  "hp",
  "maxHpPerLevel",
  "maxHpPerLevelRange",
  "defense",
  "attackSpeedPercent",
  "attackSpeed",
  "cooldownReduction",
  "cooldownReductionPercent",
  "critChancePercent",
  "critChance",
  "criticalStrikeChance",
  "critDamagePercent",
  "critDamage",
  "criticalStrikeDamage",
  "armorPenetration",
  "armorPenetrationPercent",
  "skillAmp",
  "skillAmpPerLevel",
  "skillAmpPerLevelRange",
  "omniVampPercent",
  "omnivamp",
  "lifeStealPercent",
  "lifeSteal",
  "moveSpeedPercent",
  "moveSpeedFlat",
  "moveSpeed",
  "basicAttackRange",
  "crowdControlResistancePercent",
  "crowdControlResistance",
];

const ITEM_STAT_DISPLAY_ORDER_INDEX = ITEM_STAT_DISPLAY_ORDER.reduce((index, key, order) => ({
  ...index,
  [key]: order,
}), {});

function hasVisibleStatValue(value) {
  return value !== null && value !== undefined && value !== "" && Number(value) !== 0;
}

function formatSignedStatValue(value, suffix = "") {
  return `+${value}${suffix}`;
}

function decodeUnicodeEscapes(value) {
  return String(value || "").replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => (
    String.fromCharCode(parseInt(hex, 16))
  ));
}

function cleanDisplayText(value) {
  return decodeUnicodeEscapes(value)
    .replace(/\s+/g, " ")
    .trim();
}

function formatItemStatValue(key, value) {
  const suffix = key.endsWith("Percent") ? "%" : "";
  return formatSignedStatValue(value, suffix);
}

function getItemStatRows(stats) {
  if (!stats) {
    return [];
  }

  const usedKeys = new Set();
  const rows = [];

  for (const range of ITEM_STAT_RANGE_PAIRS) {
    const minValue = stats[range.minKey];
    const maxValue = stats[range.maxKey];

    if (hasVisibleStatValue(minValue) && hasVisibleStatValue(maxValue)) {
      usedKeys.add(range.minKey);
      usedKeys.add(range.maxKey);
      rows.push({
        key: range.labelKey,
        label: ITEM_STAT_LABELS[range.labelKey] || range.labelKey,
        value: formatSignedStatValue(`${minValue}~${maxValue}`, range.suffix),
      });
    }
  }

  for (const [key, value] of Object.entries(stats)) {
    if (usedKeys.has(key) || !hasVisibleStatValue(value)) {
      continue;
    }

    rows.push({
      key,
      label: getSimulatorStatLabel(key),
      value: formatItemStatValue(key, value),
    });
  }

  return rows.sort((left, right) => (
    (ITEM_STAT_DISPLAY_ORDER_INDEX[left.key] ?? Number.MAX_SAFE_INTEGER)
    - (ITEM_STAT_DISPLAY_ORDER_INDEX[right.key] ?? Number.MAX_SAFE_INTEGER)
  ));
}

function isItemSkillTitleCandidate(value) {
  const text = cleanDisplayText(value);

  return Boolean(
    text
    && text.length <= 36
    && !text.includes("VF")
    && !text.includes("\uc0c1\uc704 \uc544\uc774\ud15c")
    && !text.includes("\uc81c\uc791 \ud2b8\ub9ac")
    && !text.includes("\uc81c\uc791 \uac1c\uc218")
    && !/[.!?。]/.test(text)
  );
}

function getRawItemSkillName(rawItemSkill) {
  const rows = Array.isArray(rawItemSkill)
    ? rawItemSkill.map(cleanDisplayText).filter(Boolean)
    : [];

  const markerIndex = rows.findIndex((row) => row === "\uace0\uc720 \uc7a5\ucc29 \ud6a8\uacfc");
  const candidate = markerIndex >= 0 ? rows[markerIndex + 1] : "";

  return isItemSkillTitleCandidate(candidate) ? candidate : "";
}

function getDescriptionItemSkillName(description) {
  const rows = cleanDisplayText(description)
    .split(/[\n\r|]/)
    .map(cleanDisplayText)
    .filter(Boolean);
  const candidates = rows.filter(isItemSkillTitleCandidate);

  return candidates[0] || "";
}

function getItemSkillNames(item) {
  const itemSkills = Array.isArray(item?.itemSkills) ? item.itemSkills : [];
  const skillNames = itemSkills
    .map((skill) => cleanDisplayText(skill?.name))
    .filter(isItemSkillTitleCandidate);

  if (skillNames.length > 0) {
    return [...new Set(skillNames)];
  }

  const itemSkillName = cleanDisplayText(item?.itemSkill?.name);

  if (isItemSkillTitleCandidate(itemSkillName)) {
    return [itemSkillName];
  }

  const fallbackName = (
    getRawItemSkillName(item?.rawItemSkill)
    || getDescriptionItemSkillName(item?.itemSkill?.description)
  );

  return fallbackName ? [fallbackName] : [];
}

function formatFormulaNumber(value) {
  const rounded = Math.round((Number(value) || 0) * 10) / 10;

  return Number.isInteger(rounded)
    ? String(rounded)
    : rounded.toLocaleString("ko-KR", { maximumFractionDigits: 1 });
}

const ITEM_SKILL_DEBUFF_FULL_PATTERN = /(?:적\s*)?대상의?\s+[^.!?。]*?감소/g;
const ITEM_SKILL_DECREASE_PATTERN = /(?:방어력|이동\s*속도|치유|공격\s*속도|방어\s*관통)\s*감소|감소/g;

function rangesOverlap(left, right) {
  return left.start < right.end && left.end > right.start;
}

function findItemSkillDebuffMatches(text) {
  const fullMatches = [...text.matchAll(ITEM_SKILL_DEBUFF_FULL_PATTERN)].map((match) => ({
    className: "coefficient-decrease",
    end: match.index + match[0].length,
    start: match.index,
    type: "highlight",
  }));
  const partialMatches = [...text.matchAll(ITEM_SKILL_DECREASE_PATTERN)].map((match) => ({
    className: "coefficient-decrease",
    end: match.index + match[0].length,
    start: match.index,
    type: "highlight",
  }));

  return [...fullMatches, ...partialMatches]
    .filter((item) => item.start >= 0)
    .sort((left, right) => left.start - right.start || right.end - left.end)
    .reduce((items, item) => {
      if (items.some((existing) => rangesOverlap(existing, item))) {
        return items;
      }

      return [...items, item];
    }, []);
}

function getItemSkillStatValue(type, totalStats) {
  if (type === "bonusAttack") {
    return Number(totalStats?.bonusAttackPower) || 0;
  }

  if (type === "attack") {
    return Number(totalStats?.attackPower) || 0;
  }

  if (type === "bonusHealth") {
    return Number(totalStats?.bonusMaxHp) || 0;
  }

  if (type === "health") {
    return Number(totalStats?.maxHp ?? totalStats?.hp) || 0;
  }

  if (type === "defense") {
    return Number(totalStats?.defense) || 0;
  }

  if (type === "skillAmp") {
    return Number(totalStats?.skillAmp) || 0;
  }

  return 0;
}

function getCoefficientClassName(type) {
  if (type === "level") {
    return "coefficient-level";
  }

  if (type === "attack" || type === "bonusAttack") {
    return "coefficient-attack";
  }

  if (type === "health" || type === "bonusHealth") {
    return "coefficient-health";
  }

  return "";
}

function createInlineFormulaText(statValue, coefficient, isPercent = true) {
  const formattedStatValue = formatFormulaNumber(statValue);
  const formattedCoefficient = formatFormulaNumber(coefficient);
  const result = isPercent ? statValue * coefficient / 100 : statValue * coefficient;

  return {
    result,
    text: `${formattedStatValue} × ${formattedCoefficient}${isPercent ? "%" : ""} = ${formatFormulaNumber(result)}`,
  };
}

function findItemSkillFormulaMatches(text, { level, totalStats }) {
  const matches = [];
  const debuffMatches = findItemSkillDebuffMatches(text);
  const isInDebuff = (start, end) => debuffMatches.some((debuff) => rangesOverlap(debuff, { start, end }));
  const normalizedLevel = Number(level) || 1;
  const levelPattern = /(?:캐릭터\s*)?레벨\s*\*\s*(\d+(?:\.\d+)?)/g;
  let match = levelPattern.exec(text);

  while (match) {
    const start = match.index;
    const end = match.index + match[0].length;

    if (isInDebuff(start, end)) {
      match = levelPattern.exec(text);
      continue;
    }

    const coefficient = Number(match[1]) || 0;
    const formula = createInlineFormulaText(normalizedLevel, coefficient, false);

    matches.push({
      className: getCoefficientClassName("level"),
      end,
      formulaText: formula.text,
      isCalculable: true,
      result: formula.result,
      start,
      type: "formula",
    });
    match = levelPattern.exec(text);
  }

  [
    { pattern: /(?:적\s*)?대상의?\s*(?:현재\s*)?체력(?:의)?\s*(\d+(?:\.\d+)?)\s*%/g, targetRequired: true, type: "health" },
    { pattern: /(?:적\s*)?대상의?\s*최대\s*체력(?:의)?\s*(\d+(?:\.\d+)?)\s*%/g, targetRequired: true, type: "health" },
    { pattern: /적\s*(?:현재\s*)?체력(?:의)?\s*(\d+(?:\.\d+)?)\s*%/g, targetRequired: true, type: "health" },
    { pattern: /적\s*최대\s*체력(?:의)?\s*(\d+(?:\.\d+)?)\s*%/g, targetRequired: true, type: "health" },
    { pattern: /(?:자신의\s*)?(?:현재\s*)?잃은\s*체력(?:의)?\s*(\d+(?:\.\d+)?)\s*%/g, targetRequired: true, type: "health" },
    { pattern: /(?:손실한|잃어버린)\s*체력(?:의)?\s*(\d+(?:\.\d+)?)\s*%/g, targetRequired: true, type: "health" },
    { pattern: /추가\s*공격력(?:의)?\s*(\d+(?:\.\d+)?)\s*%/g, type: "bonusAttack" },
    { pattern: /공격력(?:의)?\s*(\d+(?:\.\d+)?)\s*%/g, type: "attack" },
    { pattern: /추가\s*체력(?:의)?\s*(\d+(?:\.\d+)?)\s*%/g, type: "bonusHealth" },
    { pattern: /(?:최대\s*체력|체력)(?:의)?\s*(\d+(?:\.\d+)?)\s*%/g, type: "health" },
    { pattern: /방어력(?:의)?\s*(\d+(?:\.\d+)?)\s*%/g, type: "defense" },
    { pattern: /스킬\s*증폭(?:의)?\s*(\d+(?:\.\d+)?)\s*%/g, type: "skillAmp" },
  ].forEach(({ pattern, targetRequired = false, type }) => {
    let statMatch = pattern.exec(text);

    while (statMatch) {
      const start = statMatch.index;
      const end = statMatch.index + statMatch[0].length;

      if (isInDebuff(start, end)) {
        statMatch = pattern.exec(text);
        continue;
      }

      const coefficient = Number(statMatch[1]) || 0;
      const statValue = getItemSkillStatValue(type, totalStats);
      const formula = createInlineFormulaText(statValue, coefficient);

      matches.push({
        className: getCoefficientClassName(type),
        end,
        formulaText: targetRequired ? "" : formula.text,
        isCalculable: !targetRequired,
        result: targetRequired ? 0 : formula.result,
        start,
        type: "formula",
      });
      statMatch = pattern.exec(text);
    }
  });

  matches.push(...debuffMatches);

  return matches
    .filter((item) => item.start >= 0)
    .sort((left, right) => left.start - right.start || right.end - left.end)
    .reduce((items, item) => {
      const previous = items[items.length - 1];

      if (previous && item.start < previous.end) {
        return items;
      }

      return [...items, item];
    }, []);
}

function findBaseDamageMatches(text, formulaMatches) {
  return [...text.matchAll(/(^|[^\d.])(\d+(?:\.\d+)?)(?=\s*\(\s*\+)/g)]
    .map((match) => {
      const start = match.index + match[1].length;

      return {
        className: "base-damage",
        end: start + match[2].length,
        result: Number(match[2]) || 0,
        start,
        type: "baseDamage",
      };
    })
    .filter((item) => !formulaMatches.some((formula) => item.start < formula.end && item.end > formula.start));
}

function getFormulaContext(text, index) {
  const nextText = text.slice(index, Math.min(text.length, index + 100));
  const healIndex = nextText.search(/체력을\s*회복|회복합니다|회복/);
  const damageIndex = nextText.search(/피해를\s*입힙니다|추가\s*스킬\s*피해|스킬\s*피해|피해/);

  if (healIndex < 0 && damageIndex < 0) {
    return "";
  }

  if (healIndex >= 0 && (damageIndex < 0 || healIndex < damageIndex)) {
    return "heal";
  }

  if (damageIndex >= 0) {
    return "damage";
  }

  return "";
}

function getExpressionEnd(text, start) {
  const tail = text.slice(start);
  const match = tail.match(/^\d+(?:\.\d+)?(?:\s*\(\s*\+[^)]*\))*/);

  return match ? start + match[0].length : start;
}

function buildItemSkillResultSummaries(text, formulaMatches, baseDamageMatches) {
  const usedFormulaIndexes = new Set();
  const summaries = [];

  baseDamageMatches.forEach((baseMatch) => {
    const expressionEnd = getExpressionEnd(text, baseMatch.start);
    const context = getFormulaContext(text, expressionEnd);

    if (!context) {
      return;
    }

    const formulas = formulaMatches.filter((formula, index) => {
      const isInExpression = formula.start >= baseMatch.start && formula.start < expressionEnd;

      if (isInExpression) {
        usedFormulaIndexes.add(index);
      }

      return isInExpression && formula.isCalculable;
    });
    const total = baseMatch.result + formulas.reduce((sum, formula) => sum + formula.result, 0);

    summaries.push({
      context,
      text: `${context === "heal" ? "회복량" : "최종 피해량"}: ${formatFormulaNumber(total)}`,
    });
  });

  formulaMatches.forEach((formula, index) => {
    if (usedFormulaIndexes.has(index) || !formula.isCalculable) {
      return;
    }

    const context = getFormulaContext(text, formula.end);

    if (!context) {
      return;
    }

    summaries.push({
      context,
      text: `${context === "heal" ? "회복량" : "최종 피해량"}: ${formatFormulaNumber(formula.result)}`,
    });
  });

  return summaries.filter((summary, index, list) => (
    list.findIndex((item) => item.context === summary.context && item.text === summary.text) === index
  ));
}

function analyzeItemSkillDescription(description, context) {
  const text = cleanDisplayText(description);
  const itemSkillMatches = findItemSkillFormulaMatches(text, context);
  const formulaMatches = itemSkillMatches.filter((match) => match.type === "formula");
  const baseDamageMatches = findBaseDamageMatches(text, itemSkillMatches);
  const matches = [...itemSkillMatches, ...baseDamageMatches]
    .sort((left, right) => left.start - right.start || right.end - left.end)
    .reduce((items, item) => {
      const previous = items[items.length - 1];

      if (previous && item.start < previous.end) {
        return items;
      }

      return [...items, item];
    }, []);
  const summaries = buildItemSkillResultSummaries(text, formulaMatches, baseDamageMatches);

  return {
    matches,
    summaries,
    text,
  };
}

function renderAnalyzedItemSkillDescription(analysis) {
  const text = analysis?.text || "";
  const matches = analysis?.matches || [];

  if (!matches.length) {
    return text;
  }

  const nodes = [];
  let cursor = 0;

  matches.forEach((match, index) => {
    if (match.start > cursor) {
      nodes.push(text.slice(cursor, match.start));
    }

    const matchedText = text.slice(match.start, match.end);

    if (match.type === "formula") {
      nodes.push(
        <React.Fragment key={`${match.start}-${index}`}>
          <span className={`item-skill-highlight ${match.className}`}>{matchedText}</span>
          {match.formulaText ? <span className="inline-formula"> ({match.formulaText})</span> : null}
        </React.Fragment>
      );
    } else {
      nodes.push(
        <span className={`item-skill-highlight ${match.className}`} key={`${match.start}-${index}`}>
          {matchedText}
        </span>
      );
    }

    cursor = match.end;
  });

  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }

  return nodes;
}

function getItemSkillEntries(item) {
  const itemSkills = Array.isArray(item?.itemSkills)
    ? item.itemSkills.filter(Boolean)
    : [];

  if (itemSkills.length > 0) {
    return itemSkills;
  }

  return item?.itemSkill ? [item.itemSkill] : [];
}

function getActiveItemSkillEffects(items, context) {
  return (items || []).filter(Boolean).flatMap((item) => (
    getItemSkillEntries(item).map((skill, index) => {
      const description = cleanDisplayText(skill?.description);
      const analysis = analyzeItemSkillDescription(description, context);
      const fallbackName = getDescriptionItemSkillName(description);
      const name = cleanDisplayText(skill?.name) || fallbackName || "고유 장착 효과";
      const itemName = getItemDisplayName(item);

      return {
        analysis,
        code: item.code || item.weaponKey || `${itemName}-${index}`,
        description,
        image: item.image,
        itemName,
        key: `${item.code || item.weaponKey || itemName}-${name}-${index}`,
        name,
        useDakFallback: item.slotType !== "weapon",
      };
    })
  ));
}

function getSimulatorStatLabel(key) {
  return ITEM_STAT_LABELS[key] || key;
}

function getEchionSkillData(skillKey, weaponType) {
  if (skillKey === "Q" || skillKey === "W" || skillKey === "E") {
    return echionSkills?.common?.[skillKey] || {};
  }

  return echionSkills?.[weaponType]?.[skillKey] || {};
}

function EquipmentSelectModal({
  currentItem,
  items,
  onClear,
  onClose,
  onSearchChange,
  onSelect,
  searchValue,
  slotKey,
  slotLabel,
}) {
  return (
    <div className="equipment-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        aria-label={`${slotLabel || slotKey} item select`}
        className="equipment-modal"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="equipment-modal-header">
          <div className="equipment-modal-title">{slotLabel || slotKey}</div>
          <button className="equipment-modal-close" type="button" onClick={onClose}>
            x
          </button>
        </div>

        <input
          className="equipment-search"
          placeholder="Search item name or code"
          type="search"
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
        />

        <div className="equipment-option-list">
          {items.length > 0 ? items.map((item) => {
            const itemKey = item.code || item.weaponKey;
            const currentItemKey = currentItem?.code || currentItem?.weaponKey;
            const isSelected = currentItemKey === itemKey;
            const itemGrade = getItemGradeValue(item);
            const itemName = getItemDisplayName(item);
            const itemStatRows = getItemStatRows(item.stats);
            const itemSkillNames = getItemSkillNames(item);

            return (
              <button
                className={`equipment-option ${getItemGradeClass(itemGrade)} ${isSelected ? "selected" : ""}`}
                key={itemKey}
                type="button"
                onClick={() => onSelect(slotKey, item)}
              >
                <ItemImage
                  src={item.image}
                  alt={itemName}
                  code={itemKey}
                  useDakFallback={item.slotType !== "weapon"}
                />
                <span className="equipment-option-body">
                  <span className="equipment-option-name">{itemName}</span>
                  {item.needsReview ? (
                    <span className="equipment-option-meta">검수 필요</span>
                  ) : null}
                  {itemStatRows.length > 0 ? (
                    <span className="equipment-option-stat-list">
                      {itemStatRows.map((row) => (
                        <span className="equipment-option-stat" key={row.key}>
                          - {row.label} {row.value}
                        </span>
                      ))}
                    </span>
                  ) : null}
                  {itemSkillNames.map((skillName) => (
                    <span className="equipment-option-skill" key={skillName}>
                      - {skillName}
                    </span>
                  ))}
                </span>
              </button>
            );
          }) : (
            <div className="equipment-option-empty">No matching items</div>
          )}
        </div>

        <div className="equipment-modal-actions">
          <button type="button" onClick={() => onClear(slotKey)}>
            Clear
          </button>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function getSkillBounds(skillKey, weaponType) {
  const skillData = getEchionSkillData(skillKey, weaponType);
  const fixedLevel = skillData.fixedLevel || (skillData.isPassive ? 1 : null);

  if (fixedLevel) {
    return { fixedLevel, maxLevel: fixedLevel, minLevel: fixedLevel };
  }

  return {
    fixedLevel: null,
    maxLevel: skillData.maxLevel || (skillKey === "R" ? 3 : 5),
    minLevel: 1,
  };
}

function SkillLevelControl({ level, onChange, skillKey, weaponType }) {
  const bounds = getSkillBounds(skillKey, weaponType);
  const maxLevel = bounds.maxLevel;
  const isFixed = Boolean(bounds.fixedLevel);

  if (isFixed) {
    return (
      <div className="skill-level-control fixed">
        Lv. {bounds.fixedLevel}
      </div>
    );
  }

  return (
    <div className="skill-level-control">
      <button
        type="button"
        disabled={level <= 1}
        onClick={() => onChange(skillKey, -1)}
      >
        -
      </button>
      <span>Lv. {level}</span>
      <button
        type="button"
        disabled={level >= maxLevel}
        onClick={() => onChange(skillKey, 1)}
      >
        +
      </button>
    </div>
  );
}

function getSkillLevelData(skillData, level) {
  return skillData?.levels?.[level] || {};
}

function normalizeSkillDescriptionText(value) {
  return String(value || "")
    .split("\n")
    .map((line) => line.trimStart())
    .join("\n");
}

function SkillDescriptionBlock({ description }) {
  const lines = normalizeSkillDescriptionText(description).split("\n");

  return (
    <div className="skill-detail-description">
      <div className="skill-detail-title">설명:</div>
      <div className="skill-detail-description-lines">
        {lines.map((line, index) => (
          <div className="skill-detail-description-line" key={`${index}-${line}`}>
            {line || "\u00a0"}
          </div>
        ))}
      </div>
    </div>
  );
}

// 스킬 상세 패널에 표시되는 levelData 한글명 수정 위치
const skillStatLabelMap = {
  attackPowerRatio: "공격력 계수",
  attackSpeedIncreasePercent: "공격 속도 증가",
  baseDamage: "기본 피해",
  bonusAttackPowerRatio: "추가 공격력 계수",
  bonusHealthRatio: "추가 체력 계수",
  bonusHpRatio: "추가 체력 계수",
  cooldown: "쿨다운",
  duration: "지속 시간",
  extraDamagePercent: "후속 피해 비율",
  fixedDamage: "고정 피해",
  fixedDamageAttackPowerRatio: "고정 피해 공격력 계수",
  fixedDamagePerTick: "고정 피해",
  maxHpRatio: "최대 체력 계수",
  moveSpeedSlowPercent: "이동 속도 감소",
  moveDuration: "이동 시간",
  moveDistance: "이동 거리",
  overloadMoveDistance: "과부하 이동 거리",
  shield: "보호막",
  skillAmpRatio: "스킬 증폭 계수",
  skillAttackBonusDamagePercent: "스킬 피해 증폭",
  skillDamageVampPercent: "스킬 피해 흡혈",
  vfGaugeGain: "VF 게이지 증가",
  vfGaugeReturnPercent: "VF 게이지 반환",
};

const SKILL_DETAIL_INTERNAL_KEYS = new Set([
  "description",
  "fixedLevel",
  "hits",
  "isPassive",
  "levels",
  "maxLevel",
  "name",
  "rawText",
]);

function getSkillDetailFieldLabel(key) {
  return skillStatLabelMap[key] || key;
}

function getCooldownReductionPercent(totalStats) {
  const cooldownReduction = Number(totalStats?.cooldownReduction) || 0;
  const cooldownReductionPercent = Number(totalStats?.cooldownReductionPercent) || 0;

  return cooldownReductionPercent || cooldownReduction;
}

function getVfDamageBonusMultiplier(rLevel, vfGauge) {
  const maxBonusByLevel = {
    1: 0.3,
    2: 0.5,
    3: 0.7,
    4: 0.9,
  };
  const normalizedLevel = Math.min(4, Math.max(1, Number(rLevel) || 1));
  const normalizedGauge = Math.min(100, Math.max(0, Number(vfGauge) || 0));

  return 1 + (maxBonusByLevel[normalizedLevel] || 0) * normalizedGauge / 100;
}

function getSidewinderPassiveMultiplier(rLevel) {
  const bonusByLevel = {
    1: 0.2,
    2: 0.3,
    3: 0.4,
    4: 0.5,
  };
  const normalizedLevel = Math.min(4, Math.max(1, Number(rLevel) || 1));

  return 1 + (bonusByLevel[normalizedLevel] || 0);
}

function shouldApplyVfBonus(skillKey) {
  return skillKey === "Q" || skillKey === "E";
}

function shouldApplySidewinderPassive(skillKey, weaponType) {
  return weaponType === "sidewinder" && ["Q", "E", "R"].includes(skillKey);
}

function formatSkillDetailValue(key, value) {
  if (key.endsWith("Ratio")) {
    return `${formatFormulaNumber((Number(value) || 0) * 100)}%`;
  }

  if (key.endsWith("Percent")) {
    return `${formatFormulaNumber(value)}%`;
  }

  if (key === "cooldown") {
    return `${formatFormulaNumber(value)}초`;
  }

  if (key === "duration" || key === "moveDuration") {
    return `${formatFormulaNumber(value)}초`;
  }

  if (key === "moveDistance" || key === "overloadMoveDistance") {
    return `${formatFormulaNumber(value)}m`;
  }

  if (Array.isArray(value)) {
    return value.join(" / ");
  }

  return String(value);
}

function getSkillDetailRows(levelData) {
  return Object.entries(levelData || {}).filter(([key, value]) => (
    value !== null && value !== undefined && value !== ""
    && !SKILL_DETAIL_INTERNAL_KEYS.has(key)
    && key !== "attackPowerRatio"
    && key !== "cooldown"
    && key !== "vfGaugeGain"
    && key !== "moveSpeedSlowPercent"
    && key !== "bonusAttackPowerRatio"
    && key !== "bonusHealthRatio"
    && key !== "fixedDamagePerTick"
    && key !== "fixedDamageAttackPowerRatio"
    && key !== "shield"
  ));
}

function SkillDamageLine({
  hit,
  isFixedDamage = false,
  rLevel,
  selectedSkillKey,
  title,
  totalStats,
  vfGauge,
  weaponType,
}) {
  const attackPower = Number(totalStats?.attackPower) || 0;
  const bonusAttackPower = Number(totalStats?.bonusAttackPower) || 0;
  const bonusMaxHp = Number(totalStats?.bonusMaxHp) || 0;
  const baseDamage = Number(hit.baseDamage) || 0;
  const attackPowerRatio = Number(hit.attackPowerRatio) || 0;
  const bonusAttackPowerRatio = Number(hit.bonusAttackPowerRatio) || 0;
  const bonusHealthRatio = Number(hit.bonusHealthRatio) || 0;
  const calculatedDamage = (
    baseDamage
    + attackPower * attackPowerRatio
    + bonusAttackPower * bonusAttackPowerRatio
    + bonusMaxHp * bonusHealthRatio
  );
  const vfMultiplier = getVfDamageBonusMultiplier(rLevel, vfGauge);
  const sidewinderMultiplier = getSidewinderPassiveMultiplier(rLevel);
  const vfBonusRatio = vfMultiplier - 1;
  const sidewinderBonusRatio = sidewinderMultiplier - 1;
  const appliesVfBonus = !isFixedDamage && shouldApplyVfBonus(selectedSkillKey);
  const appliesSidewinderBonus = !isFixedDamage && shouldApplySidewinderPassive(selectedSkillKey, weaponType);
  const vfBonusDamage = calculatedDamage * vfBonusRatio;
  const sidewinderBonusDamage = calculatedDamage * sidewinderBonusRatio;
  const expectedDamage = (
    calculatedDamage
    + (appliesSidewinderBonus ? sidewinderBonusDamage : 0)
    + (appliesVfBonus ? vfBonusDamage : 0)
  );
  const damageParts = [
    {
      className: "coefficient-attack",
      label: "공격력",
      ratio: attackPowerRatio,
      show: attackPowerRatio > 0,
    },
    {
      className: "coefficient-attack",
      label: "추가 공격력",
      ratio: bonusAttackPowerRatio,
      show: bonusAttackPowerRatio > 0,
    },
    {
      className: "coefficient-health",
      label: "추가 체력",
      ratio: bonusHealthRatio,
      show: bonusHealthRatio > 0,
    },
  ].filter((part) => part.show);

  return (
    <div className="skill-detail-damage-line">
      <div className="skill-detail-title">{title || hit.label}:</div>
      <div>
        <span className="base-damage">{formatFormulaNumber(baseDamage)}</span>
        {damageParts.map((part) => (
          <React.Fragment key={part.label}>
            {" + "}
            <span className={`item-skill-highlight ${part.className}`}>
              {part.label} {formatFormulaNumber(part.ratio * 100)}%
            </span>
          </React.Fragment>
        ))}
        {" = "}
        <strong>{formatFormulaNumber(calculatedDamage)}</strong>
      </div>
      <div>
        최소 피해량: <strong>{formatFormulaNumber(calculatedDamage)}</strong>
      </div>
      {(appliesSidewinderBonus || appliesVfBonus) ? (
        <div className="skill-detail-bonus-list">
          {appliesSidewinderBonus ? (
            <div>
              사와추가피해: 최소 피해량 × {formatFormulaNumber(sidewinderBonusRatio)}
              {" = "}
              <strong>{formatFormulaNumber(sidewinderBonusDamage)}</strong>
            </div>
          ) : null}
          {appliesVfBonus ? (
            <div>
              VF추가피해: 최소 피해량 × {formatFormulaNumber(vfBonusRatio)}
              {" = "}
              <strong>{formatFormulaNumber(vfBonusDamage)}</strong>
            </div>
          ) : null}
          <div>
            최대 피해량: {formatFormulaNumber(calculatedDamage)}
            {appliesSidewinderBonus ? ` + ${formatFormulaNumber(sidewinderBonusDamage)}` : ""}
            {appliesVfBonus ? ` + ${formatFormulaNumber(vfBonusDamage)}` : ""}
            {" = "}
            <strong>{formatFormulaNumber(expectedDamage)}</strong>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SkillShieldLine({ levelData, totalStats }) {
  if (!levelData?.shield) {
    return null;
  }

  const attackPower = Number(totalStats?.attackPower) || 0;
  const shield = Number(levelData.shield) || 0;
  const attackPowerRatio = Number(levelData.attackPowerRatio) || 0;
  const calculatedShield = shield + attackPower * attackPowerRatio;

  return (
    <div className="skill-detail-damage-line">
      <div className="skill-detail-title">보호막:</div>
      <div>
        <span className="base-damage">{formatFormulaNumber(shield)}</span>
        {" + "}
        <span className="item-skill-highlight coefficient-attack">
          공격력 {formatFormulaNumber(attackPowerRatio * 100)}%
        </span>
        {" = "}
        <strong>{formatFormulaNumber(calculatedShield)}</strong>
      </div>
    </div>
  );
}

function SkillCooldownLine({ cooldown, totalStats }) {
  if (!cooldown) {
    return null;
  }

  const cooldownReductionPercent = getCooldownReductionPercent(totalStats);
  const reductionSeconds = cooldown * cooldownReductionPercent / 100;
  const finalCooldown = Math.max(0, cooldown - reductionSeconds);

  return (
    <div className="skill-detail-cooldown-line">
      쿨다운: {formatFormulaNumber(cooldown)}초
      {" "}
      (-{formatFormulaNumber(reductionSeconds)}초)
      {" = "}
      <strong>{formatFormulaNumber(finalCooldown)}초</strong>
    </div>
  );
}

function getAttackSpeedPerSecond(totalStats) {
  const attackSpeedPercent = Number(totalStats?.attackSpeedPercent) || 0;
  const attackSpeedFlat = Number(totalStats?.attackSpeed) || 0;

  return Math.max(0, attackSpeedFlat || (1 + attackSpeedPercent / 100));
}

function calculateSkillHitDamageValue({
  hit,
  isFixedDamage = false,
  rLevel,
  selectedSkillKey,
  totalStats,
  vfGauge,
  weaponType,
}) {
  const attackPower = Number(totalStats?.attackPower) || 0;
  const bonusAttackPower = Number(totalStats?.bonusAttackPower) || 0;
  const bonusMaxHp = Number(totalStats?.bonusMaxHp) || 0;
  const baseDamage = Number(hit?.baseDamage) || 0;
  const attackPowerRatio = Number(hit?.attackPowerRatio) || 0;
  const bonusAttackPowerRatio = Number(hit?.bonusAttackPowerRatio) || 0;
  const bonusHealthRatio = Number(hit?.bonusHealthRatio) || 0;
  const calculatedDamage = (
    baseDamage
    + attackPower * attackPowerRatio
    + bonusAttackPower * bonusAttackPowerRatio
    + bonusMaxHp * bonusHealthRatio
  );
  const appliesVfBonus = !isFixedDamage && shouldApplyVfBonus(selectedSkillKey);
  const appliesSidewinderBonus = !isFixedDamage && shouldApplySidewinderPassive(selectedSkillKey, weaponType);

  return (
    calculatedDamage
    + (appliesSidewinderBonus ? calculatedDamage * (getSidewinderPassiveMultiplier(rLevel) - 1) : 0)
    + (appliesVfBonus ? calculatedDamage * (getVfDamageBonusMultiplier(rLevel, vfGauge) - 1) : 0)
  );
}

function getFinalSkillCooldown(cooldown, totalStats) {
  const baseCooldown = Number(cooldown) || 0;

  if (!baseCooldown) {
    return 0;
  }

  return Math.max(0, baseCooldown * (1 - getCooldownReductionPercent(totalStats) / 100));
}

function getSkillDpsSummary({
  levelData,
  rLevel,
  selectedSkillKey,
  totalStats,
  vfGauge,
  weaponType,
}) {
  const cooldown = getFinalSkillCooldown(levelData?.cooldown, totalStats);

  if (!cooldown) {
    return null;
  }

  const hitDamage = (levelData?.hits || []).reduce((total, hit) => (
    total + calculateSkillHitDamageValue({
      hit,
      rLevel,
      selectedSkillKey,
      totalStats,
      vfGauge,
      weaponType,
    })
  ), 0);
  const fixedDamagePerTick = Number(levelData?.fixedDamagePerTick) || 0;
  const fixedAttackRatio = Number(levelData?.fixedDamageAttackPowerRatio) || 0;
  const fixedTickDamage = fixedDamagePerTick
    ? calculateSkillHitDamageValue({
      hit: {
        baseDamage: fixedDamagePerTick,
        attackPowerRatio: fixedAttackRatio,
      },
      isFixedDamage: true,
      rLevel,
      selectedSkillKey,
      totalStats,
      vfGauge,
      weaponType,
    })
    : 0;
  const fixedDuration = selectedSkillKey === "R" ? 9 : Number(levelData?.duration) || 0;
  const fixedTickInterval = fixedTickDamage ? 0.5 : 0;
  const fixedTotal = fixedTickDamage && fixedDuration
    ? fixedTickDamage * Math.floor(fixedDuration / fixedTickInterval)
    : 0;
  const totalDamage = hitDamage + fixedTotal;

  if (!totalDamage) {
    return null;
  }

  return {
    cooldown,
    dps: totalDamage / cooldown,
    totalDamage,
  };
}

function getAllSkillDpsSummaries({
  skillLevels,
  totalStats,
  vfGauge,
  weaponType,
}) {
  const rLevel = skillLevels.R || 1;
  const summaries = ["Q", "W", "E", "R"].flatMap((skillKey) => {
    const skillData = getEchionSkillData(skillKey, weaponType);
    const bounds = getSkillBounds(skillKey, weaponType);
    const level = bounds.fixedLevel || skillLevels[skillKey] || bounds.minLevel;
    const levelData = getSkillLevelData(skillData, level);
    const summary = getSkillDpsSummary({
      levelData,
      rLevel,
      selectedSkillKey: skillKey,
      totalStats,
      vfGauge,
      weaponType,
    });

    return summary ? [{ ...summary, skillKey }] : [];
  });

  return {
    summaries,
    totalDps: summaries.reduce((total, summary) => total + summary.dps, 0),
  };
}

function CombatPredictionPanel({
  isOpen,
  onToggle,
  selectedSkillKey,
  skillLevels,
  totalStats,
  vfGauge,
  weaponType,
}) {
  if (!selectedSkillKey) {
    return null;
  }

  const attackPower = Number(totalStats?.attackPower) || 0;
  const basicAttackAmpPercent = Number(totalStats?.basicAttackAmpPercent) || 0;
  const basicAttackAmpRatio = basicAttackAmpPercent / 100;
  const basicAttackDamage = attackPower + attackPower * basicAttackAmpRatio;
  const deathadderBonusDamage = weaponType === "deathadder"
    ? attackPower * 0.4 * basicAttackAmpRatio
    : 0;
  const critChancePercent = Number(totalStats?.critChancePercent) || 0;
  const critDamageRatio = (Number(totalStats?.critDamagePercent) || 175) / 100;
  const attackSpeedPerSecond = getAttackSpeedPerSecond(totalStats);
  const skillDps = getAllSkillDpsSummaries({
    skillLevels,
    totalStats,
    vfGauge,
    weaponType,
  });

  return (
    <section className={`simulator-collapsible-panel combat-prediction-panel ${isOpen ? "open" : ""}`}>
      <button
        className="simulator-collapsible-title"
        type="button"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <span>예측 DPS</span>
        <span aria-hidden="true">{isOpen ? "▲" : "▼"}</span>
      </button>
      {isOpen && (
        <div className="simulator-collapsible-body combat-prediction-body">
      <div className="combat-prediction-row">
        <div>
          <strong>기본 공격 피해</strong>
          <span>
            {formatFormulaNumber(attackPower)} + {formatFormulaNumber(attackPower)} × {formatFormulaNumber(basicAttackAmpPercent)}%
            {" = "}
            <b>{formatFormulaNumber(basicAttackDamage)}</b>
          </span>
        </div>
        {deathadderBonusDamage ? (
          <div>
            <strong>카드모스의 부름 [데스애더] 추가 피해</strong>
            <span>
              {formatFormulaNumber(attackPower)} × 40% × {formatFormulaNumber(basicAttackAmpPercent)}%
              {" = "}
              <b>{formatFormulaNumber(deathadderBonusDamage)}</b>
            </span>
          </div>
        ) : null}
      </div>
      {critChancePercent > 0 ? (
        <div className="combat-prediction-row">
          <div>
            <strong>치명타 적용 시 기본 공격 피해</strong>
            <span><b>{formatFormulaNumber(basicAttackDamage * critDamageRatio)}</b></span>
          </div>
          {deathadderBonusDamage ? (
            <div>
              <strong>치명타 적용 시 카드모스의 부름 [데스애더] 추가 피해</strong>
              <span><b>{formatFormulaNumber(deathadderBonusDamage * critDamageRatio)}</b></span>
            </div>
          ) : null}
        </div>
      ) : null}
      <div className="combat-prediction-row">
        <div>
          <strong>기본 공격 예측 DPS</strong>
          <span>
            {formatFormulaNumber(basicAttackDamage)} × {formatFormulaNumber(attackSpeedPerSecond)}/초
            {" = "}
            <b>{formatFormulaNumber(basicAttackDamage * attackSpeedPerSecond)}</b>
          </span>
        </div>
        {deathadderBonusDamage ? (
          <div>
            <strong>카드모스의 부름 [데스애더] 예측 DPS</strong>
            <span>
              {formatFormulaNumber(deathadderBonusDamage)} × {formatFormulaNumber(attackSpeedPerSecond)}/초
              {" = "}
              <b>{formatFormulaNumber(deathadderBonusDamage * attackSpeedPerSecond)}</b>
            </span>
          </div>
        ) : null}
      </div>
      {skillDps.summaries.length > 0 ? (
        <div className="combat-prediction-row">
          <div>
            <strong>스킬 예측 DPS</strong>
            {skillDps.summaries.map((skill) => (
              <span key={skill.skillKey}>
                {skill.skillKey} {formatFormulaNumber(skill.totalDamage)} / {formatFormulaNumber(skill.cooldown)}초
                {" = "}
                <b>{formatFormulaNumber(skill.dps)}</b>
              </span>
            ))}
            <span>
              합계: <b>{formatFormulaNumber(skillDps.totalDps)}</b>
            </span>
          </div>
        </div>
      ) : null}
        </div>
      )}
    </section>
  );
}

function SkillDetailPanel({ isOpen, onToggle, selectedSkillKey, skillLevels, totalStats, vfGauge, weaponType }) {
  if (!selectedSkillKey) {
    return (
      <div className="skill-detail-panel">
        스킬을 선택하면 설명이 표시됩니다.
      </div>
    );
  }

  const skillData = getEchionSkillData(selectedSkillKey, weaponType);
  const bounds = getSkillBounds(selectedSkillKey, weaponType);
  const level = bounds.fixedLevel || skillLevels[selectedSkillKey] || bounds.minLevel;
  const levelData = getSkillLevelData(skillData, level);
  const detailRows = getSkillDetailRows(levelData);
  const skillName = skillData.name || "스킬명 미입력";
  const description = skillData.description || "설명 미입력";
  const hitRows = Array.isArray(levelData.hits) ? levelData.hits : [];
  const rLevel = skillLevels.R || 1;
  const fixedDamageHit = levelData.fixedDamagePerTick ? {
    label: "고정 피해량",
    baseDamage: levelData.fixedDamagePerTick,
    attackPowerRatio: levelData.fixedDamageAttackPowerRatio || 0,
    damageType: "fixed",
  } : null;

  return (
    <section className={`simulator-collapsible-panel skill-detail-panel ${isOpen ? "open" : ""}`}>
      <button
        className="simulator-collapsible-title"
        type="button"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <span>스킬 설명</span>
        <span aria-hidden="true">{isOpen ? "▲" : "▼"}</span>
      </button>
      {isOpen && (
        <div className="simulator-collapsible-body skill-detail-body">
      <div className="skill-detail-title">스킬명: {skillName}</div>
      <div className="skill-detail-meta">레벨: {level}</div>
      <SkillShieldLine levelData={levelData} totalStats={totalStats} />
      {fixedDamageHit ? (
        <SkillDamageLine
          hit={fixedDamageHit}
          isFixedDamage
          rLevel={rLevel}
          selectedSkillKey={selectedSkillKey}
          title="고정 피해량"
          totalStats={totalStats}
          vfGauge={vfGauge}
          weaponType={weaponType}
        />
      ) : null}
      {hitRows.length > 0 ? (
        <div className="skill-detail-hit-list">
          {hitRows.map((hit) => {
            const title = String(hit.label || "").includes("피해량")
              ? hit.label
              : `${hit.label} 피해량`;

            return (
              <SkillDamageLine
                hit={hit}
                key={hit.label}
                rLevel={rLevel}
                selectedSkillKey={selectedSkillKey}
                title={title}
                totalStats={totalStats}
                vfGauge={vfGauge}
                weaponType={weaponType}
              />
            );
          })}
        </div>
      ) : null}
      <SkillCooldownLine cooldown={levelData.cooldown} totalStats={totalStats} />
      {Array.isArray(levelData.vfGaugeGain) ? (
        <div className="skill-detail-values">
          <span>VF 게이지 증가량: {levelData.vfGaugeGain.join(" / ")}</span>
        </div>
      ) : null}
      {levelData.moveSpeedSlowPercent ? (
        <div className="skill-detail-values">
          <span>
            이동 속도 감소:{" "}
            <span className="item-skill-highlight coefficient-decrease">
              {levelData.moveSpeedSlowPercent}%
            </span>
          </span>
        </div>
      ) : null}
      {detailRows.length > 0 ? (
        <div className="skill-detail-values">
          {detailRows.map(([key, value]) => (
            <span key={key}>{getSkillDetailFieldLabel(key)}: {formatSkillDetailValue(key, value)}</span>
          ))}
        </div>
      ) : hitRows.length === 0 ? (
        <div className="skill-detail-empty">피해량 데이터 없음</div>
      ) : null}
      <SkillDescriptionBlock description={description} />
        </div>
      )}
    </section>
  );
}

function ActiveItemSkillEffectsPanel({ effects, isOpen, onToggle }) {
  return (
    <section className={`simulator-collapsible-panel active-item-skill-panel item-effect-panel ${isOpen ? "open" : ""}`}>
      <button
        className="simulator-collapsible-title active-item-skill-title"
        type="button"
        aria-expanded={isOpen}
        onClick={onToggle}
      >
        <span>아이템 효과</span>
        <span aria-hidden="true">{isOpen ? "▲" : "▼"}</span>
      </button>
      {isOpen && (
        <div className="simulator-collapsible-body">
      <div className="active-item-skill-title">현재 활성화된 고유 장착 효과</div>
      {effects.length > 0 ? (
        <div className="active-item-skill-list">
          {effects.map((effect) => (
            <div className="active-item-skill-card item-effect-card" key={effect.key}>
              <ItemImage
                src={effect.image}
                alt={`${effect.itemName} ${effect.name}`}
                code={effect.code}
                useDakFallback={effect.useDakFallback}
              />
              <div className="active-item-skill-body item-effect-card-text">
                <div className="active-item-skill-name">{effect.name}</div>
                <div className="active-item-skill-description">
                  {effect.description
                    ? renderAnalyzedItemSkillDescription(effect.analysis)
                    : "설명 없음"}
                </div>
                {effect.analysis?.summaries?.length > 0 ? (
                  <div className="active-item-skill-formulas">
                    {effect.analysis.summaries.map((summary) => (
                      <div key={`${summary.context}-${summary.text}`}>{summary.text}</div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="active-item-skill-empty">
          현재 활성화된 고유 장착 효과가 없습니다.
        </div>
      )}
        </div>
      )}
    </section>
  );
}

function SimulatorStatRow({ label, baseValue, bonusValue, finalValue, showBreakdown = true, suffix = "" }) {
  const formattedFinalValue = `${finalValue.toLocaleString()}${suffix}`;
  const formattedBaseValue = `${baseValue.toLocaleString()}${suffix}`;
  const formattedBonusValue = `${bonusValue.toLocaleString()}${suffix}`;

  return (
    <div className="stat-row">
      <span>{label}</span>
      <strong>
        <span className="stat-row-final">{formattedFinalValue}</span>
        {showBreakdown ? (
          <em>({formattedBaseValue} + {formattedBonusValue})</em>
        ) : null}
      </strong>
    </div>
  );
}

function getNoticeParagraphs(value) {
  if (Array.isArray(value)) {
    return value;
  }

  return String(value || "")
    .split(/\n{2,}/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function NoticeText({ value }) {
  return getNoticeParagraphs(value).map((paragraph, index) => (
    <p key={`${paragraph.slice(0, 16)}-${index}`}>
      {paragraph.split("\n").map((line, lineIndex) => (
        <React.Fragment key={`${line}-${lineIndex}`}>
          {lineIndex > 0 && <br />}
          {line}
        </React.Fragment>
      ))}
    </p>
  ));
}

function NoticePanel() {
  return (
    <div className="content-section notice-panel">
      <h3>안내사항</h3>
      <NoticeText value={NOTICE.intro} />

      <h4>※ 플레이 데이터 수집 안내</h4>
      <NoticeText value={NOTICE.watchlist} />
      <h4>MetaPoint / MVP 기준</h4>
      <NoticeText value={NOTICE.metapoint} />
      <NoticeText value={NOTICE.rpTop} />
      <NoticeText value={NOTICE.apiLimit} />

      <p className="notice-discord">
        <strong>{NOTICE.discord.label}</strong>
        <a href={NOTICE.discord.url} target="_blank" rel="noreferrer">
          {NOTICE.discord.url}
        </a>
      </p>
    </div>
  );
}

function SeasonArchive({ isMobile }) {
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState("");
  const [archiveData, setArchiveData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [nicknameInput, setNicknameInput] = useState("");
  const [searchedNickname, setSearchedNickname] = useState("");
  const [searchedSeason, setSearchedSeason] = useState("");
  const [searchRevision, setSearchRevision] = useState(0);
  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState("");
  const [matchPage, setMatchPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 0, total: 0 });

  useEffect(() => {
    let ignore = false;

    fetchJson("ekion/archive/seasons")
      .then((data) => {
        if (ignore) return;
        const seasonList = normalizePatchList(Array.isArray(data) ? data : [])
          .filter((patch) => /^V\d+$/.test(patch));
        setSeasons(seasonList);
        setSelectedSeason((current) => (
          current && seasonList.includes(current) ? current : seasonList[0] || ""
        ));
        if (seasonList.length === 0) setIsLoading(false);
      })
      .catch((requestError) => {
        if (ignore) return;
        console.error(requestError);
        setError("이전 시즌 목록을 불러오지 못했습니다.");
        setIsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedSeason) return undefined;
    let ignore = false;
    setIsLoading(true);
    setError("");
    setArchiveData(null);
    setNicknameInput("");
    setSearchedNickname("");
    setSearchedSeason("");
    setMatches([]);
    setMatchesError("");
    setMatchPage(1);
    setPagination({ page: 1, total_pages: 0, total: 0 });

    fetchJson(`ekion/archive/season?season=${encodeURIComponent(selectedSeason)}`)
      .then((data) => {
        if (!ignore) setArchiveData(data);
      })
      .catch((requestError) => {
        if (ignore) return;
        console.error(requestError);
        setError("이전 시즌 통계를 불러오지 못했습니다.");
      })
      .finally(() => {
        if (!ignore) setIsLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [selectedSeason]);

  useEffect(() => {
    if (!selectedSeason || !searchedNickname || searchedSeason !== selectedSeason) return undefined;
    let ignore = false;
    const params = new URLSearchParams({
      patch_version: selectedSeason,
      nickname: searchedNickname,
      page: String(matchPage),
      limit: String(MATCH_PAGE_LIMIT),
    });
    setMatchesLoading(true);
    setMatchesError("");

    fetchJson(`ekion/matches?${params.toString()}`)
      .then((data) => {
        if (ignore) return;
        const items = Array.isArray(data) ? data : data.items || [];
        setMatches(items);
        setPagination(Array.isArray(data)
          ? { page: 1, total_pages: 1, total: items.length }
          : {
            page: data.page || matchPage,
            total_pages: data.total_pages || 0,
            total: data.total || 0,
          });
      })
      .catch((requestError) => {
        if (ignore) return;
        console.error(requestError);
        setMatches([]);
        setMatchesError("과거 전적을 불러오지 못했습니다.");
      })
      .finally(() => {
        if (!ignore) setMatchesLoading(false);
      });

    return () => {
      ignore = true;
    };
  }, [matchPage, searchRevision, searchedNickname, searchedSeason, selectedSeason]);

  function handleSearch(event) {
    event.preventDefault();
    const nickname = nicknameInput.trim();
    if (!nickname) return;
    setMatches([]);
    setMatchPage(1);
    setSearchedNickname(nickname);
    setSearchedSeason(selectedSeason);
    setSearchRevision((current) => current + 1);
  }

  if (!isLoading && seasons.length === 0) {
    return <div className="empty-panel">조회 가능한 이전 시즌 데이터가 없습니다.</div>;
  }

  const summary = archiveData?.summary;
  const pageNumbers = getPaginationPages(pagination.page, pagination.total_pages);

  return (
    <div className="archive-page">
      <div className="archive-season-header">
        <label>
          <span>시즌</span>
          <select value={selectedSeason} onChange={(event) => setSelectedSeason(event.target.value)}>
            {seasons.map((season) => (
              <option key={season} value={season}>Season {season.replace("V", "")}</option>
            ))}
          </select>
        </label>
      </div>

      {isLoading && <div className="empty-panel">이전 시즌 통계를 불러오는 중입니다.</div>}
      {error && <div className="empty-panel">{error}</div>}
      {!isLoading && archiveData && (
        <>
          <section className="archive-summary" aria-label={`${selectedSeason} 시즌 요약`}>
            <strong>Season {selectedSeason.replace("V", "")}</strong>
            <span>분석 경기 {Number(summary?.ranked_games || 0).toLocaleString()}</span>
            <span>평균 순위 {Number(summary?.avg_rank || 0).toFixed(2)}</span>
            <span>승률 {(Number(summary?.win_rate || 0) * 100).toFixed(1)}%</span>
            <span>
              평균 RP {Number(summary?.avg_rp_gain || 0) > 0 ? "+" : ""}
              {Number(summary?.avg_rp_gain || 0).toFixed(1)}
            </span>
          </section>

          <section className="archive-weapon-grid">
            {(archiveData.weapons || []).map((weapon) => (
              <ArchiveWeaponCard key={weapon.weapon_group} weapon={weapon} isMobile={isMobile} />
            ))}
          </section>

          <section className="archive-match-search">
            <div className="archive-match-search-heading">
              <div>
                <h3>과거 전적 검색</h3>
                <span>Season {selectedSeason.replace("V", "")}</span>
              </div>
              <form onSubmit={handleSearch}>
                <input
                  type="search"
                  value={nicknameInput}
                  onChange={(event) => setNicknameInput(event.target.value)}
                  placeholder="닉네임"
                  aria-label="과거 전적 닉네임"
                />
                <button type="submit">검색</button>
              </form>
            </div>

            {!searchedNickname ? (
              <div className="archive-search-guide">과거 전적은 닉네임 검색 시 표시됩니다.</div>
            ) : matchesLoading ? (
              <div className="empty-panel">과거 전적을 불러오는 중입니다.</div>
            ) : matchesError ? (
              <div className="empty-panel">{matchesError}</div>
            ) : matches.length > 0 ? (
              <>
                <div className="match-list archive-match-list">
                  {matches.map((match) => (
                    <MatchCard
                      key={match.match_id}
                      match={match}
                      isMobile={isMobile}
                      showPatchVersion
                    />
                  ))}
                </div>
                {pagination.total_pages > 1 && (
                  <div className="match-pagination" aria-label="과거 전적 페이지">
                    <button
                      type="button"
                      disabled={pagination.page <= 1}
                      onClick={() => setMatchPage((current) => Math.max(1, current - 1))}
                    >이전</button>
                    {pageNumbers.map((page) => (
                      <button
                        type="button"
                        className={pagination.page === page ? "active" : ""}
                        key={page}
                        onClick={() => setMatchPage(page)}
                      >{page}</button>
                    ))}
                    <button
                      type="button"
                      disabled={pagination.page >= pagination.total_pages}
                      onClick={() => setMatchPage((current) => Math.min(pagination.total_pages, current + 1))}
                    >다음</button>
                  </div>
                )}
              </>
            ) : (
              <div className="empty-panel">해당 시즌에 저장된 전적이 없습니다.</div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function ArchiveWeaponCard({ weapon, isMobile }) {
  const weaponClass = getWeaponClassByName(weapon.weapon_group);
  return (
    <article className={`archive-weapon-card ${weaponClass}`}>
      <div className="archive-weapon-heading">
        <ItemImage
          src={weapon.image}
          alt={weapon.representative_item_name}
          code={weapon.representative_item_code}
          showTooltip={false}
        />
        <div>
          <h3>{weapon.weapon_group}</h3>
          <span>{weapon.representative_item_name}</span>
        </div>
        <div className="archive-season-weapon-stats">
          <strong style={getMetaPointScoreStyle(weapon.seasonMetaPoint)}>
            Season MetaPoint {formatMetaPointScore(weapon.seasonMetaPoint)}
          </strong>
          <span>Season Pick Rate {Number(weapon.seasonPickRate || 0).toFixed(1)}%</span>
        </div>
      </div>
      <SeasonMetaPointChart
        history={weapon.metaPointHistory || []}
        isMobile={isMobile}
      />
    </article>
  );
}

function getHistoryChangesForPatch(patchVersion) {
  const normalizedPatch = normalizePatchVersion(patchVersion);
  return PATCH_HISTORY
    .filter((entry) => normalizePatchVersion(entry.version) === normalizedPatch)
    .flatMap((entry) => Array.isArray(entry.changes) ? entry.changes : []);
}

function SeasonMetaPointChart({ history, isMobile }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const rows = (Array.isArray(history) ? history : [])
    .filter((row) => Number.isFinite(row?.score) && row?.patchVersion)
    .slice()
    .sort((left, right) => compareNormalizedPatchVersions(left.patchVersion, right.patchVersion));

  if (rows.length === 0) {
    return <div className="archive-chart-empty">계산 가능한 MetaPoint 데이터가 없습니다.</div>;
  }

  const width = 320;
  const height = 160;
  const left = 24;
  const right = 14;
  const top = 14;
  const bottom = 36;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const points = rows.map((row, index) => ({
    row,
    x: left + (rows.length === 1 ? chartWidth / 2 : (chartWidth * index) / (rows.length - 1)),
    y: top + chartHeight - (Math.max(0, Math.min(100, row.score)) / 100) * chartHeight,
  }));
  const hoveredPoint = hoveredIndex === null ? null : points[hoveredIndex];
  const hoveredPreviousRow = hoveredIndex > 0 ? rows[hoveredIndex - 1] : null;
  const hoveredDelta = hoveredPoint && hoveredPreviousRow
    ? hoveredPoint.row.score - hoveredPreviousRow.score
    : null;
  const selectedPoint = selectedIndex === null ? null : points[selectedIndex];
  const selectedPreviousRow = selectedIndex > 0 ? rows[selectedIndex - 1] : null;

  return (
    <div className="archive-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="시즌 패치별 MetaPoint 변화">
        {[0, 25, 50, 75, 100].map((tick) => {
          const y = top + chartHeight - (tick / 100) * chartHeight;
          return (
            <g key={tick}>
              <line
                className={`archive-chart-gridline ${tick === 50 ? "reference" : ""}`}
                x1={left}
                x2={width - right}
                y1={y}
                y2={y}
              />
              <text className="archive-chart-y-label" x={left - 5} y={y + 3} textAnchor="end">{tick}</text>
            </g>
          );
        })}
        {points.length > 1 && (
          <polyline
            className="archive-chart-line"
            points={points.map((point) => `${point.x},${point.y}`).join(" ")}
          />
        )}
        {points.map((point, index) => (
          <g
            className="archive-chart-point"
            key={point.row.patchVersion}
            role="button"
            tabIndex={0}
            aria-label={`${point.row.patchVersion} MetaPoint ${point.row.score}`}
            onMouseEnter={() => !isMobile && setHoveredIndex(index)}
            onMouseLeave={() => !isMobile && setHoveredIndex(null)}
            onFocus={() => !isMobile && setHoveredIndex(index)}
            onBlur={() => !isMobile && setHoveredIndex(null)}
            onClick={() => setSelectedIndex(index)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setSelectedIndex(index);
              }
            }}
          >
            <circle className="archive-chart-hit-area" cx={point.x} cy={point.y} r={13} />
            <circle
              className="archive-chart-visible-point"
              cx={point.x}
              cy={point.y}
              r={hoveredIndex === index || selectedIndex === index ? 5 : 4}
            />
            <text className="archive-chart-x-label" x={point.x} y={height - 12} textAnchor="middle">
              {point.row.patchVersion}
            </text>
          </g>
        ))}
      </svg>

      {hoveredPoint && !isMobile && (
        <div
          className="archive-chart-tooltip"
          style={{ left: `clamp(130px, ${(hoveredPoint.x / width) * 100}%, calc(100% - 130px))` }}
          role="status"
        >
          <strong>{hoveredPoint.row.patchVersion}</strong>
          <span>MetaPoint {Number(hoveredPoint.row.score).toFixed(1)}</span>
          <span>Pick Rate {Number(hoveredPoint.row.pickRate || 0).toFixed(1)}%</span>
          <span>Win Rate {Number(hoveredPoint.row.winRate || 0).toFixed(1)}%</span>
          {hoveredDelta !== null && (
            <span>
              이전 패치 대비 {hoveredDelta > 0 ? "+" : ""}{hoveredDelta.toFixed(1)}
            </span>
          )}
        </div>
      )}
      {selectedPoint && (
        <PatchMetaPointDetailPopup
          row={selectedPoint.row}
          previousRow={selectedPreviousRow}
          onClose={() => setSelectedIndex(null)}
        />
      )}
    </div>
  );
}

function PatchMetaPointDetailPopup({ row, previousRow, onClose }) {
  const historyChanges = getHistoryChangesForPatch(row.patchVersion);
  const delta = previousRow ? row.score - previousRow.score : null;

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return createPortal(
    <div className="archive-detail-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="archive-detail-popup"
        role="dialog"
        aria-modal="true"
        aria-label={`${row.patchVersion} 상세`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="archive-detail-popup-head">
          <h3>{row.patchVersion} 상세</h3>
          <button type="button" onClick={onClose} aria-label="닫기">×</button>
        </div>
        <div className="archive-detail-stats">
          <span>MetaPoint: {Number(row.score).toFixed(1)}</span>
          <span>Pick Rate: {Number(row.pickRate || 0).toFixed(1)}%</span>
          <span>Win Rate: {Number(row.winRate || 0).toFixed(1)}%</span>
          {delta !== null && (
            <span>
              이전 패치 대비 MetaPoint: {delta > 0 ? "+" : ""}{delta.toFixed(1)}
            </span>
          )}
        </div>
        {historyChanges.length > 0 && (
          <div className="archive-detail-history">
            <h4>패치 내역</h4>
            {historyChanges.map((change, index) => (
              <div key={`${row.patchVersion}-${index}`}>{change.text}</div>
            ))}
          </div>
        )}
      </section>
    </div>,
    document.body
  );
}

function HistoryTimeline({ history }) {
  const groupedHistory = groupPatchHistory(history);

  if (!groupedHistory.length) {
    return <div className="empty-panel">{UI_TEXT.history.empty}</div>;
  }

  return (
    <div className="content-section history-container">
      <div className="history-note">
        {UI_TEXT.history.note}
      </div>
      {groupedHistory.map((group) => (
        <article className="patch-card" key={group.mainVersion}>
          <div className="patch-card-head">
            <div className="patch-date">{group.date}</div>
            <div className="patch-version">
              {group.versions.map((entry) => entry.version).join(" / ")}
            </div>
          </div>
          <div className="patch-change-list">
            {group.entries.map((entry) => (
              <div className="patch-entry" key={`${entry.version}-${entry.date}`}>
                {group.entries.length > 1 && (
                  <div className="patch-entry-version">{entry.version}</div>
                )}
                {entry.changes.map((change, changeIndex) => (
                  <div
                    className={`patch-change ${getPatchChangeType(change)}`}
                    key={`${entry.version}-${change.text}-${changeIndex}`}
                  >
                    {renderPatchChange(change)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </article>
      ))}
    </div>
  );
}

function groupPatchHistory(history) {
  const groups = [];
  const groupMap = new Map();

  history.forEach((entry) => {
    const mainVersion = getMainPatchVersion(entry.version);

    if (!groupMap.has(mainVersion)) {
      const group = {
        mainVersion,
        date: entry.date,
        entries: [],
        versions: [],
      };
      groupMap.set(mainVersion, group);
      groups.push(group);
    }

    const group = groupMap.get(mainVersion);
    group.entries.push(entry);
    group.versions.push({ version: entry.version, date: entry.date });

    if (comparePatchDates(entry.date, group.date) > 0) {
      group.date = entry.date;
    }
  });

  groups.forEach((group) => {
    group.entries.sort((a, b) => comparePatchVersions(b.version, a.version));
    group.versions.sort((a, b) => comparePatchVersions(b.version, a.version));
  });

  return groups.sort((a, b) => {
    const dateCompare = comparePatchDates(b.date, a.date);
    return dateCompare || comparePatchVersions(b.mainVersion, a.mainVersion);
  });
}

function getMainPatchVersion(version) {
  return String(version || "").replace(/[a-z]+$/i, "");
}

function comparePatchDates(leftDate, rightDate) {
  return parsePatchDate(leftDate) - parsePatchDate(rightDate);
}

function parsePatchDate(dateText) {
  const parts = String(dateText || "").split(".").map((part) => Number(part));

  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    return 0;
  }

  const [year, month, day] = parts;
  return new Date(2000 + year, month - 1, day).getTime();
}

function comparePatchVersions(leftVersion, rightVersion) {
  const left = parsePatchVersion(leftVersion);
  const right = parsePatchVersion(rightVersion);

  if (left.major !== right.major) {
    return left.major - right.major;
  }

  if (left.minor !== right.minor) {
    return left.minor - right.minor;
  }

  return left.suffix.localeCompare(right.suffix);
}

function parsePatchVersion(version) {
  const match = String(version || "").match(/^(\d+)(?:\.(\d+))?([a-z]*)$/i);

  if (!match) {
    return { major: 0, minor: 0, suffix: "" };
  }

  return {
    major: Number(match[1]) || 0,
    minor: Number(match[2]) || 0,
    suffix: match[3] || "",
  };
}

function renderPatchChange(change) {
  const text = change.text || "";
  const arrowIndex = text.lastIndexOf("\u2192");

  if (arrowIndex < 0) {
    return text;
  }

  const beforeText = text.slice(0, arrowIndex + 1);
  const afterText = text.slice(arrowIndex + 1);
  const afterClass = getPatchChangeType(change);

  return (
    <>
      <span>{beforeText}</span>
      <span className={`patch-after ${afterClass}`}>{afterText}</span>
    </>
  );
}

function getPatchChangeType(change) {
  if (change.type === "buff" || change.type === "nerf") {
    return change.type;
  }

  const beforeValue = parseNumericChangeValue(change.before);
  const afterValue = parseNumericChangeValue(change.after);

  if (beforeValue === null || afterValue === null || beforeValue === afterValue) {
    return "";
  }

  return afterValue > beforeValue ? "buff" : "nerf";
}

function parseNumericChangeValue(value) {
  const match = String(value || "").replace(/,/g, "").match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : null;
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function getGameTypeLabel(gameType) {
  if (gameType === "ranked") {
    return UI_TEXT.gameType.ranked;
  }

  return UI_TEXT.gameType.normal;
}

function formatPlayedAt(playedAt) {
  if (!playedAt) {
    return "";
  }

  const date = parseUtcDate(playedAt);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const parts = Object.fromEntries(
    KST_DATE_TIME_FORMATTER
      .formatToParts(date)
      .map((part) => [part.type, part.value])
  );
  const absoluteTime = `${parts.year}.${parts.month}.${parts.day} ${parts.hour}:${parts.minute}`;
  const relativeTime = formatRelativePlayedAt(date);

  return relativeTime ? `${absoluteTime} (${relativeTime})` : absoluteTime;
}

function parseUtcDate(value) {
  const timestamp = String(value).trim();
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(timestamp);

  return new Date(hasTimezone ? timestamp : `${timestamp}Z`);
}

function formatRelativePlayedAt(date) {
  const elapsedSeconds = Math.floor((Date.now() - date.getTime()) / 1000);

  if (elapsedSeconds < 0) {
    return UI_TEXT.time.justNow;
  }

  if (elapsedSeconds < 60) {
    return UI_TEXT.time.justNow;
  }

  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) {
    return `${elapsedMinutes}${UI_TEXT.time.minutesAgoSuffix}`;
  }

  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) {
    return `${elapsedHours}${UI_TEXT.time.hoursAgoSuffix}`;
  }

  const elapsedDays = Math.floor(elapsedHours / 24);
  return `${elapsedDays}${UI_TEXT.time.daysAgoSuffix}`;
}

function formatDamageValue(value) {
  if (!value) {
    return "-";
  }

  return Math.round(value).toLocaleString();
}

function formatDamageRatio(value, total) {
  if (!value || !total) {
    return "-";
  }

  return `${((value / total) * 100).toFixed(1)}%`;
}

function DamageLine({ label, value, ratio }) {
  return (
    <div>
      <strong>{label}</strong>
      <span> : {value}</span>
      {ratio !== null && <em> ({ratio})</em>}
    </div>
  );
}

function MatchMetaItem({ item, fallback }) {
  if (!item) {
    return (
      <div className="match-meta-item empty">
        <div className="match-meta-fallback">{fallback || "-"}</div>
      </div>
    );
  }

  return (
    <div className="match-meta-item">
      <SpecialIcon item={item} />
    </div>
  );
}

function SpecialIcon({ item }) {
  const [hasImageError, setHasImageError] = useState(false);
  const name = item?.name || item?.code || "-";
  const image = item?.image && !hasImageError ? item.image : "";

  if (!image) {
    return <span className="match-meta-text-icon">{name}</span>;
  }

  return (
    <img
      className="match-meta-image"
      src={image}
      alt={name}
      loading="lazy"
      onError={() => setHasImageError(true)}
    />
  );
}

function getItemSlotLabel(slotType) {
  const slotLabels = {
    arm: "팔",
    chest: "옷",
    head: "머리",
    leg: "다리",
    weapon: "무기",
  };

  return slotLabels[slotType] || slotType || "-";
}

function getTooltipPosition(anchor, tooltip) {
  if (!anchor || !tooltip || typeof window === "undefined") {
    return { left: 0, top: 0 };
  }

  const rect = anchor.getBoundingClientRect();
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const margin = 8;
  const gap = 10;
  const tooltipWidth = tooltip.offsetWidth || 240;
  const tooltipHeight = tooltip.offsetHeight || 120;
  let left = rect.left + rect.width / 2 - tooltipWidth / 2;
  let top = rect.top - tooltipHeight - gap;

  if (top < margin) {
    top = rect.bottom + gap;
  }

  left = Math.max(margin, Math.min(left, viewportWidth - tooltipWidth - margin));
  top = Math.max(margin, Math.min(top, viewportHeight - tooltipHeight - margin));

  return { left, top };
}

function ItemInfoTooltipContent({ item, sourceItem }) {
  useEffect(() => {
    if (!item && process.env.NODE_ENV === "development") {
      console.warn("[tooltip] missing itemInfo", sourceItem);
    }
  }, [item, sourceItem]);

  if (!item) {
    const sourceCode = sourceItem?.code || sourceItem?.itemCode || sourceItem?.item_code || "";

    return (
      <>
        <strong className="item-info-tooltip-name">Unknown Item</strong>
        {sourceCode ? <span className="item-info-tooltip-code">itemCode: {sourceCode}</span> : null}
        <span className="item-info-tooltip-fail">매칭 실패</span>
      </>
    );
  }

  const itemGradeLabel = getItemGradeLabel(item.grade);
  const statRows = getItemStatRows(item.stats).slice(0, 6);
  const skillEntries = getItemSkillEntries(item).slice(0, 2);
  const rawName = item.raw?.rawName
    || item.raw?.matchedName
    || item.raw?.koreanName
    || item.raw?.itemName
    || item.raw?.item_name
    || item.raw?.name
    || "";
  const isUnknown = item.isUnknown || item.source === "unknown";

  if (isUnknown) {
    return (
      <>
        <strong className="item-info-tooltip-name">Unknown Item</strong>
        {item.code ? <span className="item-info-tooltip-code">itemCode: {item.code}</span> : null}
        {rawName ? <span>rawName: {rawName}</span> : null}
        {item.englishName ? <span>englishName: {item.englishName}</span> : null}
        <span className="item-info-tooltip-fail">매칭 실패</span>
        <span>itemStatsDraft.json에서 해당 itemCode 또는 이름을 찾지 못했습니다.</span>
      </>
    );
  }

  return (
    <>
      <strong className="item-info-tooltip-name">{item.name}</strong>
      <span className="item-info-tooltip-meta">
        {itemGradeLabel} / {getItemSlotLabel(item.slotType)}
      </span>
      {statRows.length > 0 ? (
        <span className="item-info-tooltip-stats">
          {statRows.map((row) => (
            <span key={row.key}>{row.label} {row.value}</span>
          ))}
        </span>
      ) : null}
      {skillEntries.length > 0 ? (
        <span className="item-info-tooltip-effects">
          <b>고유효과</b>
          {skillEntries.map((skill, index) => (
            <span key={`${skill.name || "effect"}-${index}`}>
              {cleanDisplayText(skill.name) || getDescriptionItemSkillName(skill.description)}
            </span>
          ))}
        </span>
      ) : null}
    </>
  );
}

function ItemInfoTooltip({ item, sourceItem }) {
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current?.parentElement;
    const tooltip = tooltipRef.current;

    if (!anchor || !tooltip) {
      return;
    }

    setPosition(getTooltipPosition(anchor, tooltip));
  }, []);

  useEffect(() => {
    const anchor = anchorRef.current?.parentElement;

    if (!anchor) {
      return undefined;
    }

    const showTooltip = () => setIsVisible(true);
    const hideTooltip = () => setIsVisible(false);

    anchor.addEventListener("mouseenter", showTooltip);
    anchor.addEventListener("mouseleave", hideTooltip);
    anchor.addEventListener("focusin", showTooltip);
    anchor.addEventListener("focusout", hideTooltip);

    return () => {
      anchor.removeEventListener("mouseenter", showTooltip);
      anchor.removeEventListener("mouseleave", hideTooltip);
      anchor.removeEventListener("focusin", showTooltip);
      anchor.removeEventListener("focusout", hideTooltip);
    };
  }, []);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isVisible, updatePosition]);

  useEffect(() => {
    if (isVisible) {
      updatePosition();
    }
  }, [item, isVisible, updatePosition]);

  return (
    <>
      <span className="item-info-tooltip-anchor" ref={anchorRef} aria-hidden="true" />
      {isVisible && typeof document !== "undefined" ? createPortal(
        <span
          className={`item-info-tooltip item-info-tooltip-portal ${item?.isUnknown || item?.source === "unknown" || !item ? "item-info-tooltip-unknown" : ""}`}
          ref={tooltipRef}
          role="tooltip"
          style={{
            left: `${position.left}px`,
            top: `${position.top}px`,
          }}
        >
          <ItemInfoTooltipContent item={item} sourceItem={sourceItem} />
        </span>,
        document.body,
      ) : null}
    </>
  );
}

function ItemImage({ src, fallbackSrc, fallbackSrcs = [], alt, code, useDakFallback = true, showTooltip = true }) {
  const [imageIndex, setImageIndex] = useState(0);
  const codeText = String(code || "").trim();
  const itemInfo = getItemInfo({ code: codeText, name: alt, image: src });
  const internalSrc = itemInfo.image || "";
  const imageAlt = itemInfo.name || alt || codeText;
  const dakSrc = useDakFallback ? buildDakItemImageUrl(codeText) : "";
  const imageCandidates = [internalSrc, src, fallbackSrc, ...fallbackSrcs, dakSrc].filter((value, index, values) => (
    value && values.indexOf(value) === index
  ));
  const imageSrc = imageCandidates[imageIndex] || "";
  const fallbackKey = fallbackSrcs.join("|");

  useEffect(() => {
    setImageIndex(0);
  }, [codeText, src, fallbackSrc, fallbackKey, useDakFallback]);

  if (!imageSrc) {
    if (process.env.NODE_ENV === "development" && (codeText || alt)) {
      console.warn("[item-mapping] missing item", codeText, alt);
    }
    return (
      <span className="item-image-tooltip-wrapper">
        <div className="item-image-fallback">-</div>
        {showTooltip ? <ItemInfoTooltip item={itemInfo} /> : null}
      </span>
    );
  }

  return (
    <span className="item-image-tooltip-wrapper">
      <img
        className="item-image"
        src={imageSrc}
        alt={imageAlt}
        loading="lazy"
        onError={() => {
          if (process.env.NODE_ENV === "development" && imageIndex >= imageCandidates.length - 1) {
            console.warn("[item-mapping] missing item", codeText, alt);
          }
          setImageIndex((current) => current + 1);
        }}
      />
      {showTooltip ? <ItemInfoTooltip item={itemInfo} /> : null}
    </span>
  );
}

export default EchionStats;
